/**
 * V70 엔글 마블 V2 — 이벤트 10종 + 금고/상점/무인도 모달
 *
 * V2 변경:
 *   - 금고 (vault), 상점 (shop) 추가
 *   - 결과 요약: 이전 점수 → 획득 → 현재 점수 (사용자 지시 §3)
 *   - 각 이벤트 성격별 애니메이션 유지 + 보강
 *   - 텍스트 화살표 "→" 제거 ("다음" 버튼)
 *   - 무인도 결과 상세 라벨
 *   - 워프: 보드 미니맵 (23칸 자유 선택)
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './animations.css';
import type { InProgressEventState, BoardCell, SubmitEventChoiceResponse } from '../engine/types';
import { cellLabel, positionToGrid, safeCornerLabel } from '../engine/boardLayout';
import { SHOP_CONFIG, V2_SAFE_ROLES, E10_CONFIG, TRAP_CONFIG } from '../config/eventConfig';
import { CellIcon, IconE3, IconE5, IconE8, IconE9, IconVault, IconShop } from '../components/IconSet';

/**
 * V70 2026-10-19 — 결과 팝업 중앙 정렬 (AppShell 메인 콘텐츠 영역 명시 선택).
 *
 * `AppShell` 의 메인 콘텐츠 wrapper 에 `data-app-shell-main="1"` 식별자가 부여됨 (AppShell.tsx:87).
 * 이 요소의 `getBoundingClientRect()` 로 메인 콘텐츠 영역 좌/우 offset 을 직접 측정 →
 * backdrop 좌/우 offset 으로 사용한다.
 *
 *   - 사이드바 펼침/접힘 (marginLeft 변경): rect.left 자동 반영
 *   - 모바일 사이드바 숨김 (marginLeft:0): rect.left = 0 → 좌측 여백 없음
 *   - 식별자 미발견 (AppShell 외 라우트): offset = 0 (뷰포트 전체 중앙 fallback)
 *   - 다른 fixed 요소 오인 X — 명시 selector 사용
 *
 * Sidebar.tsx 코드 미접근 (다른 세션 M 상태). AppShell.tsx 는 clean 상태로 1 attribute 추가.
 * 지시 §"다른 세션 작업과 충돌하지 않도록 최소 변경" 완전 준수.
 */
function useAppShellMainBounds(active: boolean): { left: number; right: number } {
  // V70 2026-10-19 — 스크롤바 깜빡임 수정 (사용자 피드백).
  //   initial state 를 lazy init 로 — 첫 render 에서 즉시 올바른 bounds 측정.
  //   이전: initial {left:0, right:0} → 첫 paint 는 viewport 전체 폭 → useLayoutEffect 재측정 → layout shift
  //   현재: lazy useState 로 첫 render 전 측정 → paint 깜빡임 제거.
  const [bounds, setBounds] = useState<{ left: number; right: number }>(() => {
    if (typeof document === 'undefined' || !active) return { left: 0, right: 0 };
    const el = document.querySelector<HTMLElement>('[data-app-shell-main="1"]');
    if (!el) return { left: 0, right: 0 };
    const r = el.getBoundingClientRect();
    return {
      left: Math.max(0, r.left),
      right: Math.max(0, window.innerWidth - r.right),
    };
  });
  useLayoutEffect(() => {
    if (!active) return;
    const measure = () => {
      const el = document.querySelector<HTMLElement>('[data-app-shell-main="1"]');
      if (!el) {
        setBounds({ left: 0, right: 0 });
        return;
      }
      const r = el.getBoundingClientRect();
      const left = Math.max(0, r.left);
      const right = Math.max(0, window.innerWidth - r.right);
      setBounds({ left, right });
    };
    measure();
    window.addEventListener('resize', measure);
    // AppShell 메인 wrapper 자체 크기 변화 감지 (사이드바 접힘/펼침으로 marginLeft 바뀌면 반영)
    const el = document.querySelector<HTMLElement>('[data-app-shell-main="1"]');
    const ro = el ? new ResizeObserver(measure) : null;
    if (el && ro) ro.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [active]);
  return bounds;
}

/**
 * V70 QA #2 fit 공식 — AnimCard 결과 숫자 fontSize 자동 축소.
 *
 * - scale() 는 레이아웃 폭을 축소하지 않아 CSS transition 중간 상태에서 clip 발생 →
 *   fontSize 비례 축소로 교체 (레이아웃 폭 자체 축소 → clip 없음).
 * - 입력 naturalW 는 **현재 fontSize** 상태에서 측정한 자연 폭. 공식은 비례 ratio 로 축소/복원.
 * - MIN_SCORE = 10, MIN_UNIT = 8 로 가독성 보장.
 * - safeAvailW = availW - 1 로 반올림 edge 보수적 처리.
 *
 * 공식 검증은 __tests__/computeFitFontSize.test.ts 에서 수학적 안전성 보장.
 */
export const FIT_FONT_BASE_SCORE = 22;
export const FIT_FONT_BASE_UNIT = 15;
export const FIT_FONT_MIN_SCORE = 10;
export const FIT_FONT_MIN_UNIT = 8;

export function computeFitFontSize(
  naturalW: number,
  availW: number,
  current: { score: number; unit: number },
): { score: number; unit: number } {
  const safeAvailW = Math.max(1, availW - 1);
  // 현재 fontSize 기준 자연 폭. ratio = safeAvailW / naturalW.
  const currentScore = current.score || FIT_FONT_BASE_SCORE;
  const currentUnit = current.unit || FIT_FONT_BASE_UNIT;
  const ratio = safeAvailW / naturalW;
  // 현재 fontSize * ratio 가 다음 폰트 사이즈. BASE 가 상한 (fit 할 때 더 키우지 않음).
  const targetScore = Math.min(FIT_FONT_BASE_SCORE, Math.floor(currentScore * ratio));
  const targetUnit = Math.min(FIT_FONT_BASE_UNIT, Math.floor(currentUnit * ratio));
  const nextScore = Math.max(FIT_FONT_MIN_SCORE, targetScore);
  const nextUnit = Math.max(FIT_FONT_MIN_UNIT, targetUnit);
  // 수렴 안정화: 변화 없으면 그대로 반환
  if (nextScore === currentScore && nextUnit === currentUnit) return current;
  return { score: nextScore, unit: nextUnit };
}

export interface MarbleEventModalProps {
  pending: InProgressEventState;
  board: BoardCell[];
  previousScore?: number;
  currentScore?: number;
  selfPosition?: number;
  /** 현재 방패 보유 수 (E8 설명 블록 "현재 N/3" 표시용) */
  shieldsOwned?: number;
  immediate?: {
    kind: 'E8' | 'E10' | 'safe';
    delta: number;
    outcomeLabel?: string;
    shieldConsumed?: boolean;
    grossLoss?: number;
    /** QA Issue #4: BE E8 cap 초과 플래그 — "방패 최대 보유 중 · 추가 획득 실패" 안내 */
    shieldsCapReached?: boolean;
  };
  onSubmit?: (choice: string | number | null) => Promise<SubmitEventChoiceResponse>;
  /** V70G8K — 결과 화면(ResultStage)이 처음 표시된 시점 1회 호출. 부모가 보류한 점수 표시를 이때 반영 */
  onResultShown?: () => void;
  onClose: () => void;
}

type Phase = 'intro' | 'choose' | 'animating' | 'result';

export function MarbleEventModal(p: MarbleEventModalProps) {
  // V70 2026-10-19 — AppShell 메인 콘텐츠 bounds 실측으로 메인 영역 정중앙 배치.
  //   left/right 둘 다 override — 레이아웃이 좌/우 margin 가진 경우에도 정확 중앙.
  const mainBounds = useAppShellMainBounds(true);
  const dynamicBackdrop: React.CSSProperties = {
    ...backdrop,
    left: mainBounds.left,
    right: mainBounds.right,
  };
  const [phase, setPhase] = useState<Phase>('intro');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    delta: number;
    outcomeLabel?: string;
    shieldConsumed?: boolean;
    scoreAfter?: number;
    arrivalKind?: 'score' | 'shield' | 'trap' | 'start' | 'pending' | 'none';
    shieldsOwnedAfter?: number;
    drawnNumber?: number;
    grossLoss?: number;
    /** QA Issue #4: BE E8 cap 초과 플래그 */
    shieldsCapReached?: boolean;
    /** QA Issue #3: BE E9 destination cell kind */
    arrivalCellKind?: 'E8' | 'E10' | 'safe';
  } | null>(null);
  const [chosen, setChosen] = useState<string | number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // §QA4 §3: 워프 "이동 전 위치" 를 보존 — ChooseStage 노출 시점의 selfPosition.
  // submit 후 setMyState 로 selfPosition prop 이 target 으로 바뀌므로 useState 로 캡처.
  const [initialSelfPos] = useState<number>(p.selfPosition ?? 0);

  const kind = p.pending.kind;
  const isImmediate = !!p.immediate;

  useEffect(() => {
    // intro → choose/animating 초기 전이. **마운트 1회만** 실행.
    // 과거: deps 에 p.currentScore 포함 → 부모 setMyState 시 재실행 → setPhase('choose') 되돌아감 (QA 버그).
    // 수정: 빈 deps 로 1회만 수행. p.immediate/currentScore 는 참조하되 재실행 방지.
    if (isImmediate) {
      const t = window.setTimeout(() => {
        setPhase('animating');
        setResult({
          delta: p.immediate!.delta,
          outcomeLabel: p.immediate!.outcomeLabel,
          shieldConsumed: p.immediate!.shieldConsumed,
          scoreAfter: p.currentScore,
          grossLoss: p.immediate!.grossLoss,
          shieldsCapReached: p.immediate!.shieldsCapReached,  // QA Issue #4
        });
      }, 400);
      return () => window.clearTimeout(t);
    } else {
      const t = window.setTimeout(() => setPhase('choose'), 500);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== 'animating') return;
    // §QA3 §4: E10 레버, §QA5 §1: E2 가위바위보 상대 손 순환/정지, §QA5 §2: E3 보물상자 stage 머신
    // 모두 result 수신 후 내부 state 머신으로 수동 전환 (서버 응답 지연 안전)
    if (kind === 'E10' || kind === 'E2' || kind === 'E3' || kind === 'E7') return;
    const durations: Partial<Record<typeof kind, number>> = {
      E1: 1500, E4: 1800, E5: 2200,
      E6: 2200, E8: 1300, E9: 1200,
      vault: 1600, shop: 600, safe: 600,
    };
    const d = durations[kind] ?? 1500;
    const t = window.setTimeout(() => setPhase('result'), d);
    return () => window.clearTimeout(t);
  }, [phase, kind]);

  // V70G8K — 결과 공개(ResultStage 표시) 시점 알림. 마운트당 1회.
  const resultShownRef = useRef(false);
  useEffect(() => {
    if (phase !== 'result' || !result || resultShownRef.current) return;
    resultShownRef.current = true;
    p.onResultShown?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, result]);

  const handleChoice = async (choice: string | number | null) => {
    if (submitting || phase !== 'choose') return;
    setChosen(choice);
    setSubmitting(true);
    setErr(null);
    setPhase('animating');
    try {
      if (!p.onSubmit) throw new Error('선택 이벤트 핸들러 누락');
      const r = await p.onSubmit(choice);
      setResult({
        delta: r.delta,
        outcomeLabel: r.outcomeLabel,
        shieldConsumed: r.shieldConsumed,
        scoreAfter: r.scoreAfter,
        arrivalKind: r.arrivalKind,
        shieldsOwnedAfter: r.shieldsOwnedAfter,
        drawnNumber: r.drawnNumber,
        grossLoss: r.grossLoss,
        shieldsCapReached: r.shieldsCapReached,  // QA Issue #4
        arrivalCellKind: r.arrivalCellKind,       // QA Issue #3
      });
    } catch (e: unknown) {
      setErr((e as Error)?.message || '선택 실패');
      setPhase('choose');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  // safe 모서리는 selfPosition 로 title/icon 분기 (RCA §2.4: 'cellLabel(safe)==안전' 오노출 수정)
  const safePos = p.selfPosition ?? 0;
  const titleText = (() => {
    if (kind === 'vault') return '보너스 금고';
    if (kind === 'shop') return '아이템 상점';
    if (kind === 'safe') {
      if (safePos === V2_SAFE_ROLES.START) return 'START';
      if (safePos === V2_SAFE_ROLES.TRAP) return '무인도';
      if (safePos === V2_SAFE_ROLES.VAULT) return '보너스 금고';
      if (safePos === V2_SAFE_ROLES.SHOP) return '아이템 상점';
      return '안전';
    }
    return cellLabel(kind);
  })();

  return (
    <div style={dynamicBackdrop}>
      <div style={card}>
        <div style={titleRow}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CellIcon kind={kind} index={safePos} size={36} />
          </div>
          <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: 18 }}>{titleText}</h3>
        </div>

        {phase === 'intro' && <div style={introBox}>이벤트 시작…</div>}

        {phase === 'choose' && (
          <ChooseStage kind={kind} pending={p.pending} board={p.board} selfPos={p.selfPosition ?? 0} submitting={submitting} onChoose={handleChoice} err={err} currentScore={p.currentScore ?? 0} />
        )}

        {phase === 'animating' && (
          <AnimatingStage
            kind={kind}
            chosen={chosen}
            pending={p.pending}
            result={result}
            onAdvanceToResult={() => setPhase('result')}
          />
        )}

        {phase === 'result' && result && (
          <ResultStage
            result={result}
            chosen={chosen}
            previousScore={p.previousScore}
            currentScore={result.scoreAfter ?? p.currentScore}
            outcomeKind={kind}
            pending={p.pending}
            shieldsOwned={p.shieldsOwned}
            fromPos={initialSelfPos}
            onClose={p.onClose}
          />
        )}
      </div>
    </div>
  );
}

// ─────────── 선택 단계 ───────────

function ChooseStage({ kind, pending, board, selfPos, submitting, onChoose, err, currentScore }: {
  kind: InProgressEventState['kind']; pending: InProgressEventState; board: BoardCell[]; selfPos: number;
  submitting: boolean; onChoose: (c: string | number | null) => void; err: string | null; currentScore: number;
}) {
  const options = pending.options || [];

  // 보너스 금고 — 확률 수치 노출 안 함 (사용자 지시 §1)
  if (kind === 'vault') {
    return (
      <div>
        <p style={hint}>어떤 금고를 열까요?</p>
        <div style={{ display: 'grid', gap: 10, maxWidth: 340, margin: '0 auto' }}>
          {options.map((opt, idx) => {
            const isSafe = idx === 0;
            return (
              <button key={String(opt.id)} disabled={submitting} onClick={() => onChoose(opt.id)} style={optBtn('yellow', true)}>
                <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 15 }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 4 }}>
                  {isSafe ? '확정 +100점' : '보상 범위 +50 ~ +400점'}
                </div>
              </button>
            );
          })}
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  // 상점 — QA Issue #5: disabled 시각 피드백 (opacity + not-allowed) + 점수 부족 안내 라벨
  if (kind === 'shop') {
    const shieldInsuff = currentScore < SHOP_CONFIG.shieldPrice;
    const speedInsuff = currentScore < SHOP_CONFIG.speedPrice;
    const shieldDisabled = submitting || shieldInsuff;
    const speedDisabled = submitting || speedInsuff;
    const disabledStyle = (base: React.CSSProperties, disabled: boolean) =>
      ({ ...base, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' } as React.CSSProperties);
    return (
      <div>
        <p style={hint}>구매하거나 그냥 지나갈 수 있습니다. 현재 보유 점수: {currentScore.toLocaleString()}</p>
        <div style={{ display: 'grid', gap: 10, maxWidth: 340, margin: '0 auto' }}>
          <button
            disabled={shieldDisabled}
            aria-disabled={shieldDisabled}
            title={shieldInsuff ? `점수 부족 — ${SHOP_CONFIG.shieldPrice - currentScore}점 더 필요` : undefined}
            onClick={() => onChoose('shield')}
            style={disabledStyle(optBtn('violet', true), shieldDisabled)}
          >
            <div style={{ fontWeight: 700, color: '#f1f5f9' }}>🛡 방패 — {SHOP_CONFIG.shieldPrice}점</div>
            <div style={{ fontSize: 11, color: shieldInsuff ? '#f87171' : '#94a3b8', marginTop: 4 }}>
              {shieldInsuff
                ? `점수 부족 · ${SHOP_CONFIG.shieldPrice - currentScore}점 더 필요`
                : `감점 이벤트 1회 방어 (최대 ${SHOP_CONFIG.shieldMax}개)`}
            </div>
          </button>
          <button
            disabled={speedDisabled}
            aria-disabled={speedDisabled}
            title={speedInsuff ? `점수 부족 — ${SHOP_CONFIG.speedPrice - currentScore}점 더 필요` : undefined}
            onClick={() => onChoose('speedBoost')}
            style={disabledStyle(optBtn('magenta', true), speedDisabled)}
          >
            <div style={{ fontWeight: 700, color: '#f1f5f9' }}>🚀 가속 — {SHOP_CONFIG.speedPrice}점</div>
            <div style={{ fontSize: 11, color: speedInsuff ? '#f87171' : '#94a3b8', marginTop: 4 }}>
              {speedInsuff
                ? `점수 부족 · ${SHOP_CONFIG.speedPrice - currentScore}점 더 필요`
                : `다음 주사위 이동 +2 (최대 ${SHOP_CONFIG.speedMax}개)`}
            </div>
          </button>
          <button disabled={submitting} onClick={() => onChoose('pass')} style={disabledStyle(optBtn('cyan', true), submitting)}>
            <div style={{ fontWeight: 700, color: '#cbd5e1' }}>그냥 지나가기</div>
          </button>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  // 워프 — 23칸 자유 선택 (보드 미니맵)
  if (kind === 'E9') {
    return (
      <div>
        <p style={hint}>워프할 칸을 선택하세요. 현재 위치({selfPos + 1}번) 는 선택 불가. 도착 칸의 이벤트가 재발동됩니다.</p>
        {/* §QA4 §4: 워프 START 보너스 미지급 안내 */}
        <p style={{ color: '#fbbf24', fontSize: 12, textAlign: 'center', margin: '0 0 10px', fontStyle: 'italic', lineHeight: 1.5 }}>
          💡 워프 이동은 START 완주 보너스를 지급하지 않습니다.
        </p>
        <WarpMinimap board={board} selfPos={selfPos} onPick={onChoose} submitting={submitting} />
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  if (kind === 'E1' || kind === 'E3' || kind === 'E5') {
    // 확률 수치 노출 안 함 (사용자 지시 §1) — 라벨 + 보상 범위만
    return (
      <div>
        <p style={hint}>선택지의 성격을 고려해 선택하세요.</p>
        <div style={{ display: 'grid', gap: 10, maxWidth: 340, margin: '0 auto' }}>
          {options.map((opt) => {
            const rewards = opt.rewards ?? [];
            const values = rewards.map(r => r.value);
            const min = values.length ? Math.min(...values) : 0;
            const max = values.length ? Math.max(...values) : 0;
            // ISSUE-02: 음수 점수는 빨간색, 양수/0 은 기존 cyan 유지. 혼재 범위는 각 값마다 색상 분리.
            const minStr = `${min > 0 ? '+' : ''}${min}`;
            const maxStr = `${max > 0 ? '+' : ''}${max}`;
            const minColor = min < 0 ? '#f87171' : '#67e8f9';
            const maxColor = max < 0 ? '#f87171' : '#67e8f9';
            return (
              <button key={String(opt.id)} disabled={submitting} onClick={() => onChoose(opt.id)} style={optBtn('cyan', true)}>
                <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 15 }}>{opt.label}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {min === max ? (
                    <><span style={{ color: minColor }}>{minStr}점</span> 확정</>
                  ) : (
                    <>
                      <span style={{ color: minColor }}>{minStr}</span>
                      <span style={{ color: '#94a3b8' }}> ~ </span>
                      <span style={{ color: maxColor }}>{maxStr}점</span>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  if (kind === 'E2') {
    return (
      <div>
        <p style={hint}>가위/바위/보 를 고르세요.</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          {[['rock', '✊', '바위'], ['paper', '✋', '보'], ['scissors', '✌', '가위']].map(([id, emoji, label]) => (
            <button key={id} disabled={submitting} onClick={() => onChoose(id)} style={{ ...optBtn('cyan'), flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 36 }}>{emoji}</div>
              <div style={{ color: '#cbd5e1', fontSize: 13 }}>{label}</div>
            </button>
          ))}
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  if (kind === 'E4') {
    // §2: 원형 2개 동전 (앞면=W, 뒷면=★) + 선택 시 금색 glow
    return (
      <div>
        <p style={hint}>앞면 / 뒷면 중 하나를 선택하세요.</p>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', alignItems: 'center', padding: '12px 0' }}>
          {(['heads', 'tails'] as const).map((face) => (
            <button
              key={face}
              disabled={submitting}
              onClick={() => onChoose(face)}
              style={{
                background: 'transparent', border: 0, padding: 0,
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              <CoinFace face={face} size={92} />
              <div style={{ color: '#f1f5f9', fontSize: 15, fontWeight: 800, marginTop: 10, letterSpacing: 0.5 }}>
                {face === 'heads' ? '앞면' : '뒷면'}
              </div>
            </button>
          ))}
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  // E6 운명의 카드 — 안내 문구 정확 (사용자 지시 §3), 고품질 카드 디자인
  if (kind === 'E6') {
    return (
      <div>
        <p style={{ color: '#f1f5f9', fontSize: 14, margin: '0 0 10px', textAlign: 'center' }}>
          5장 카드 중 1장을 선택하세요.
        </p>
        {/* ISSUE-02: 운명의 카드 보상 안내 — 음수 (-50 / -100) 는 빨간색, 양수는 노란색, 0 은 중립 회색 */}
        <p style={{ fontSize: 13, margin: '0 0 16px', fontWeight: 600, textAlign: 'center' }}>
          <span style={{ color: '#94a3b8' }}>보상: </span>
          <span style={{ color: '#fbbf24' }}>+200</span>
          <span style={{ color: '#94a3b8' }}> / </span>
          <span style={{ color: '#fbbf24' }}>+100</span>
          <span style={{ color: '#94a3b8' }}> / </span>
          <span style={{ color: '#94a3b8' }}>0</span>
          <span style={{ color: '#94a3b8' }}> / </span>
          <span style={{ color: '#f87171' }}>-50</span>
          <span style={{ color: '#94a3b8' }}> / </span>
          <span style={{ color: '#f87171' }}>-100</span>
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, maxWidth: 400, margin: '0 auto' }}>
          {[0,1,2,3,4].map((i) => (
            <button
              key={i}
              disabled={submitting}
              onClick={() => onChoose(i)}
              className="mb-card-back"
              style={{
                aspectRatio: '2/3', borderRadius: 8, padding: 0,
                // V70 2026-10-19 — NGLE 브랜드 Teal 재디자인
                background: 'linear-gradient(135deg, #106070 0%, #00A6B5 50%, #2BBECC 100%)',
                border: '2px solid #F5C842',
                boxShadow: '0 4px 10px rgba(16, 96, 112, 0.3), inset 0 1px 2px rgba(255,255,255,0.3)',
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 180ms ease, box-shadow 180ms ease',
                position: 'relative', overflow: 'hidden',
              }}
            >
              {/* 카드 뒷면 패턴 */}
              <div style={{ position: 'absolute', inset: 6, border: '1px solid rgba(196,181,253,0.4)', borderRadius: 4 }} />
              <div style={{ fontSize: 22, color: '#c4b5fd' }}>✦</div>
              <div style={{ color: '#e0e7ff', fontSize: 10, marginTop: 4, fontWeight: 600 }}>카드 {i + 1}</div>
            </button>
          ))}
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  // E7 홀짝 — # 아이콘 제거, 큰 홀/짝 텍스트 버튼 (사용자 지시 §2)
  if (kind === 'E7') {
    return (
      <div>
        <p style={hint}>홀/짝 중 하나를 선택하세요.</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            disabled={submitting}
            onClick={() => onChoose('odd')}
            style={{
              flex: 1, padding: '28px 16px', textAlign: 'center',
              background: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)',
              border: '2px solid #ef4444',
              borderRadius: 10, color: '#fef2f2',
              cursor: submitting ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 10px rgba(220,38,38,0.3)',
            }}
          >
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 2 }}>홀</div>
          </button>
          <button
            disabled={submitting}
            onClick={() => onChoose('even')}
            style={{
              flex: 1, padding: '28px 16px', textAlign: 'center',
              background: 'linear-gradient(135deg, #082f49 0%, #0369a1 100%)',
              border: '2px solid #0ea5e9',
              borderRadius: 10, color: '#f0f9ff',
              cursor: submitting ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 10px rgba(14,165,233,0.3)',
            }}
          >
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 2 }}>짝</div>
          </button>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  return null;
}

// ─────────── 워프 보드 미니맵 ───────────

function WarpMinimap({ board, selfPos, onPick, submitting }: {
  board: BoardCell[]; selfPos: number; onPick: (i: number) => void; submitting: boolean;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(7, 1fr)', gap: 3, maxWidth: 350, margin: '0 auto', aspectRatio: '1' }}>
      {board.map((c) => {
        const { row, col } = positionToGrid(c.index);
        const isSelf = c.index === selfPos;
        const isE9 = c.kind === 'E9';
        const disabled = submitting || isSelf || isE9;
        const label = c.kind === 'safe' ? safeCornerLabel(c.index) : cellLabel(c.kind);
        return (
          <button
            key={c.index}
            disabled={disabled}
            onClick={() => onPick(c.index)}
            title={`${c.index + 1}번 · ${label}${isSelf ? ' (현재 위치)' : ''}`}
            style={{
              gridRow: row + 1, gridColumn: col + 1,
              background: isSelf ? '#fbbf24' : isE9 ? '#4c1d95' : '#1e293b',
              border: isSelf ? '2px solid #fcd34d' : '1px solid #334155',
              borderRadius: 4, color: '#f1f5f9', fontSize: 10, padding: 2, cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div style={{ fontSize: 8, opacity: 0.7 }}>{c.index + 1}</div>
            {isSelf ? (
              <div style={{ fontSize: 14 }}>🧍</div>
            ) : (
              <CellIcon kind={c.kind} index={c.index} size={18} />
            )}
          </button>
        );
      })}
      {/* 중앙 셀 1칸만 보여도 되고, grid gap 가득 채워도 됨. 가운데 공간은 CSS grid-area 로 숨김 */}
    </div>
  );
}

// ─────────── 애니메이션 단계 ───────────

function AnimatingStage({ kind, chosen, pending, result, onAdvanceToResult }: {
  kind: InProgressEventState['kind'];
  chosen: string | number | null;
  pending: InProgressEventState;
  result: { delta: number; outcomeLabel?: string; shieldConsumed?: boolean; grossLoss?: number } | null;
  onAdvanceToResult: () => void;
}) {
  const anim: Partial<Record<typeof kind, React.ReactNode>> = {
    E1: <AnimDoors chosen={String(chosen)} />,
    E2: <AnimRps chosen={String(chosen)} result={result} onComplete={onAdvanceToResult} />,
    E3: <AnimTreasure chosen={String(chosen)} pending={pending} result={result} onComplete={onAdvanceToResult} />,
    E4: <AnimCoin chosen={chosen === 'heads' || chosen === 'tails' ? chosen : null} result={result} />,
    E5: <AnimBomb chosen={String(chosen)} />,
    E6: <AnimCard chosen={Number(chosen)} result={result} />,
    E7: <AnimOddEven chosen={String(chosen)} result={result} onComplete={onAdvanceToResult} />,
    E8: <AnimShield />,
    E9: <AnimWarp />,
    E10: <AnimJackpot result={result} onComplete={onAdvanceToResult} />,
    vault: <AnimVault chosen={String(chosen)} />,
    shop: <AnimShop />,
    safe: <div style={{ fontSize: 48, textAlign: 'center' }}>✦</div>,
  };
  // V70 QA 재재발 #3 수정: 방패 3단 연출을 애니메이션 단계에서도 노출 (ResultStage 뿐 아니라).
  //   공통 ShieldOverlay 컴포넌트가 time-sequential 로 "원래 -N점 → 방패 발동 → 실제 0점" 순서 재생.
  //   모든 미니게임의 음수 결과 경로에 일관되게 적용 (각 Anim 과 무관하게 AnimatingStage 공통 영역).
  return (
    <div style={{ padding: '20px 0', minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      {anim[kind] ?? <div style={{ color: '#94a3b8' }}>이벤트 처리 중…</div>}
      <ShieldOverlay result={result} compact />
    </div>
  );
}

/**
 * V70 QA 재재발 #3 — 방패 발동 3단 연출 공통 컴포넌트 (time-sequential).
 *
 * 조건: result.shieldConsumed && result.grossLoss > 0
 *
 * 재생 순서 (시간 순):
 *   t=0ms      원래 -N점 획득
 *   t=600ms   🛡 방패 발동!
 *   t=1200ms  실제 점수 변동 0점
 *
 * compact=true 는 AnimatingStage 안쪽용 (작은 폰트), false 는 ResultStage 용 (큰 폰트).
 * 서버 점수 계산 정책 불변 — 표시 전용.
 */
function ShieldOverlay({ result, compact }: {
  result: { shieldConsumed?: boolean; grossLoss?: number } | null;
  compact?: boolean;
}) {
  const shielded = !!result?.shieldConsumed && typeof result?.grossLoss === 'number' && result.grossLoss > 0;
  const [stage, setStage] = React.useState<0 | 1 | 2 | 3>(shielded ? 0 : 3);
  React.useEffect(() => {
    if (!shielded) { setStage(3); return; }
    setStage(0);
    const t1 = window.setTimeout(() => setStage(1), 600);
    const t2 = window.setTimeout(() => setStage(2), 1200);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [shielded, result?.grossLoss]);

  if (!shielded) return null;
  const size = compact
    ? { title: 14, mid: 11, final: 16 }
    : { title: 20, mid: 15, final: 24 };
  const grossLoss = result!.grossLoss as number;
  return (
    <div className="mb-shield-overlay" style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: size.title, color: '#f87171', fontWeight: 700,
        textShadow: '0 0 12px rgba(248,113,113,0.4)',
        opacity: stage >= 0 ? 1 : 0, transition: 'opacity 300ms',
      }}>
        원래 -{grossLoss}점
      </div>
      <div style={{
        fontSize: size.mid, color: '#c4b5fd', margin: '6px 0', fontWeight: 700,
        opacity: stage >= 1 ? 1 : 0, transition: 'opacity 300ms',
      }}>
        🛡 방패 발동! 감점 {grossLoss}점 방어
      </div>
      <div style={{
        fontSize: size.final, color: '#cbd5e1', fontWeight: 900,
        opacity: stage >= 2 ? 1 : 0, transition: 'opacity 300ms',
      }}>
        실제 점수 변동 0점
      </div>
    </div>
  );
}

// ─────────── 결과 단계 (REVEAL + 점수 정산 통합, 사용자 지시 §UX) ───────────

function ResultStage({ result, chosen, previousScore, currentScore, outcomeKind, pending, shieldsOwned, fromPos, onClose }: {
  result: {
    delta: number;
    outcomeLabel?: string;
    shieldConsumed?: boolean;
    arrivalKind?: 'score' | 'shield' | 'trap' | 'start' | 'pending' | 'none';
    shieldsOwnedAfter?: number;
    drawnNumber?: number;
    /** ISSUE-05: 방패 발동 시 원래 손실 금액 (양의 정수) — 결과창 3단 연출용 */
    grossLoss?: number;
    /** QA Issue #4: BE E8 cap 초과 플래그 */
    shieldsCapReached?: boolean;
    /** QA Issue #3: BE E9 destination cell kind */
    arrivalCellKind?: 'E8' | 'E10' | 'safe';
  };
  chosen: string | number | null;
  previousScore?: number;
  currentScore?: number;
  outcomeKind?: string;
  pending?: InProgressEventState;
  shieldsOwned?: number;
  fromPos?: number;
  onClose: () => void;
}) {
  const delta = result.delta;
  const color = delta > 0 ? '#86efac' : delta < 0 ? '#f87171' : '#cbd5e1';
  const hasBoth = typeof previousScore === 'number' && typeof currentScore === 'number';

  // §QA4 §3·§8: E9 워프 — 위치 이동 이벤트, arrivalKind 기반 구조화 분기
  // QA Issue #3 수정: arrivalCellKind='E10' 인 경우 잭팟 체인 애니메이션 재생 (중복 점수 지급 없음).
  if (outcomeKind === 'E9' && result.arrivalCellKind === 'E10') {
    const toIndex = typeof chosen === 'number' ? chosen : -1;
    const fromIndex = fromPos ?? -1;
    return (
      <div style={{ textAlign: 'center' }} className="mb-anim-pop">
        <div style={{ fontSize: 44 }}>🌀 → 💎</div>
        <div style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 900, margin: '10px 0' }}>
          워프 → 잭팟!
        </div>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', margin: '10px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>이동 전</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#f472b6' }}>{fromIndex >= 0 ? `${fromIndex + 1}번` : '—'}</div>
          </div>
          <div style={{ fontSize: 22, color: '#94a3b8' }}>→</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>이동 후</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fbbf24' }}>{toIndex >= 0 ? `${toIndex + 1}번` : '—'}</div>
          </div>
        </div>
        {/* 잭팟 reel 체인 애니메이션 — BE가 이미 점수 반영했으므로 디스플레이만. onComplete 시 ResultStage 종료 */}
        <AnimJackpot
          result={{ delta: result.delta, outcomeLabel: result.outcomeLabel }}
          onComplete={onClose}
        />
      </div>
    );
  }

  if (outcomeKind === 'E9') {
    const toIndex = typeof chosen === 'number' ? chosen : -1;
    const fromIndex = fromPos ?? -1;
    const arrivalKind = result.arrivalKind;
    const outcomeLabel = result.outcomeLabel ?? '';
    return (
      <div style={{ textAlign: 'center' }} className="mb-anim-pop">
        <div style={{ fontSize: 56 }}>🌀</div>
        <div style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 900, margin: '10px 0' }}>
          워프 완료!
        </div>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', margin: '14px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>이동 전</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#f472b6' }}>{fromIndex >= 0 ? `${fromIndex + 1}번` : '—'}</div>
          </div>
          <div style={{ fontSize: 28, color: '#94a3b8' }}>→</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>이동 후</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#86efac' }}>{toIndex >= 0 ? `${toIndex + 1}번` : '—'}</div>
          </div>
        </div>
        <div style={{ color: '#cbd5e1', fontSize: 12 }}>순간이동 완료</div>
        <div style={{ color: '#fbbf24', fontSize: 11, marginTop: 10, fontStyle: 'italic' }}>
          💡 워프 이동은 START 완주 보너스를 지급하지 않습니다.
        </div>

        {/* arrivalKind 기반 블록 분기 (구조화 — 문자열 파싱 X) */}
        {arrivalKind === 'score' && <WarpScoreBlock delta={delta} outcomeLabel={outcomeLabel} />}
        {arrivalKind === 'shield' && <WarpShieldBlock shieldsOwnedAfter={result.shieldsOwnedAfter ?? 0} />}
        {arrivalKind === 'trap' && <WarpTrapBlock />}
        {/* 'start' / 'pending' / 'none' → 보조 블록 없음 (주 안내만) */}

        <button onClick={onClose} style={nextBtn}>확인</button>
      </div>
    );
  }

  // §QA3 §3: E8 방패 — 점수 UI 전부 제거, 방패 획득 설명만
  // QA Issue #4: shieldsCapReached 전달 — "방패 최대 · 획득 실패" 메인 분기용
  if (outcomeKind === 'E8') {
    return (
      <div style={{ textAlign: 'center' }} className="mb-anim-pop">
        <RevealContent
          kind="E8"
          chosen={chosen}
          outcomeLabel={result.outcomeLabel}
          delta={delta}
          pending={pending}
          shieldConsumed={result.shieldConsumed}
          shieldsOwned={shieldsOwned}
          shieldsCapReached={result.shieldsCapReached}
        />
        <button onClick={onClose} style={nextBtn}>다음</button>
      </div>
    );
  }

  // §QA3 §2: E6 운명의 카드 — 뒤집힌 카드가 결과 화면에서도 유지 (사용자 "다음" 클릭까지)
  if (outcomeKind === 'E6') {
    return (
      <div style={{ textAlign: 'center' }} className="mb-anim-pop">
        <AnimCard chosen={typeof chosen === 'number' ? chosen : -1} result={result} />
        {/* V70 QA 재재발 #3: 공통 ShieldOverlay 사용 (time-sequential 재생). AnimatingStage 와 동일 UI. */}
        {result.shieldConsumed && typeof result.grossLoss === 'number' && result.grossLoss > 0 ? (
          <div style={{ marginTop: 12 }}>
            <ShieldOverlay result={result} />
          </div>
        ) : result.shieldConsumed ? (
          <div style={{ color: '#c4b5fd', marginTop: 10, fontSize: 12 }}>🛡 방패가 손실을 막았습니다</div>
        ) : null}
        {hasBoth && (
          <div style={scoreSummaryBox}>
            <div style={scoreRow}><span>이전 점수</span><span style={scoreNum}>{previousScore!.toLocaleString()}</span></div>
            <div style={scoreRow}><span>이번 변동</span><span style={{ ...scoreNum, color }}>{delta > 0 ? '+' : ''}{delta.toLocaleString()}</span></div>
            <div style={{ height: 1, background: '#334155', margin: '6px 0' }} />
            <div style={{ ...scoreRow, color: '#fbbf24', fontWeight: 700 }}><span>현재 점수</span><span style={scoreNum}>{currentScore!.toLocaleString()}</span></div>
          </div>
        )}
        <button onClick={onClose} style={nextBtn}>다음</button>
      </div>
    );
  }

  // 그 외 — 단순 3줄 scoreSummaryBox (QA3 §1: 바퀴 pill/분해 블록 제거)
  return (
    <div style={{ textAlign: 'center' }} className="mb-anim-pop">
      <RevealContent
        kind={outcomeKind}
        chosen={chosen}
        outcomeLabel={result.outcomeLabel}
        delta={delta}
        pending={pending}
        shieldConsumed={result.shieldConsumed}
        shieldsOwned={shieldsOwned}
        drawnNumber={result.drawnNumber}
      />

      {/* V70 QA 재재발 #3: 공통 ShieldOverlay 사용 (time-sequential 재생). */}
      {result.shieldConsumed && typeof result.grossLoss === 'number' && result.grossLoss > 0 ? (
        <div style={{ marginTop: 16 }}>
          <ShieldOverlay result={result} />
        </div>
      ) : (
        <>
          <div style={{
            marginTop: 16, fontSize: 44, fontWeight: 900, color,
            textShadow: `0 0 20px ${color}66`,
          }} className="mb-score-pop">
            {delta > 0 ? '+' : ''}{delta}
          </div>
          {result.shieldConsumed && (
            <div style={{ color: '#c4b5fd', marginTop: 6, fontSize: 12 }}>🛡 방패가 손실을 막았습니다</div>
          )}
        </>
      )}
      {hasBoth && (
        <div style={scoreSummaryBox}>
          <div style={scoreRow}><span>이전 점수</span><span style={scoreNum}>{previousScore!.toLocaleString()}</span></div>
          <div style={scoreRow}><span>이번 변동</span><span style={{ ...scoreNum, color }}>{delta > 0 ? '+' : ''}{delta.toLocaleString()}</span></div>
          <div style={{ height: 1, background: '#334155', margin: '6px 0' }} />
          <div style={{ ...scoreRow, color: '#fbbf24', fontWeight: 700 }}><span>현재 점수</span><span style={scoreNum}>{currentScore!.toLocaleString()}</span></div>
        </div>
      )}
      <button onClick={onClose} style={nextBtn}>다음</button>
    </div>
  );
}

/**
 * 이벤트별 REVEAL UI.
 * 사용자 지시: 실제 결정된 결과만 공개. 결과와 무관한 가짜 정보 생성 금지.
 */
function RevealContent({ kind, chosen, outcomeLabel, delta, pending, shieldConsumed, shieldsOwned, drawnNumber, shieldsCapReached }: {
  kind?: string;
  chosen: string | number | null;
  outcomeLabel?: string;
  delta: number;
  pending?: InProgressEventState;
  shieldConsumed?: boolean;
  shieldsOwned?: number;
  drawnNumber?: number;
  /** QA Issue #4: BE 가 E8 cap 초과 시 명시. FE "최대 보유 · 획득 실패" 안내 분기용. */
  shieldsCapReached?: boolean;
}) {
  // E2 가위바위보 — 상대 패 역산 (choice + outcomeLabel)
  if (kind === 'E2' && typeof chosen === 'string') {
    const myHand = chosen as 'rock' | 'paper' | 'scissors';
    const opponent = inferRpsOpponent(myHand, outcomeLabel);
    const myEmoji = rpsEmoji(myHand);
    const oppEmoji = rpsEmoji(opponent);
    return (
      <div className="mb-reveal-pop">
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 56, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>{myEmoji}</div>
            <div style={{ color: '#67e8f9', fontSize: 12, marginTop: 4, fontWeight: 700 }}>나</div>
          </div>
          <div style={{ color: '#cbd5e1', fontSize: 20, fontWeight: 700 }}>VS</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 56, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>{oppEmoji}</div>
            <div style={{ color: '#f472b6', fontSize: 12, marginTop: 4, fontWeight: 700 }}>상대</div>
          </div>
        </div>
        <div style={{ marginTop: 12, color: outcomeLabelColor(outcomeLabel), fontWeight: 800, fontSize: 20 }}>
          {outcomeLabel ?? '결과'}
        </div>
      </div>
    );
  }

  // E4 동전 — CoinFace 2개 좌우 비교 (§2 보완: 선택/결과 공개까지 동일 그래픽)
  if (kind === 'E4' && (chosen === 'heads' || chosen === 'tails')) {
    const myFace: 'heads' | 'tails' = chosen;
    const hit = outcomeLabel === '적중';
    const resultFace: 'heads' | 'tails' = hit ? myFace : (myFace === 'heads' ? 'tails' : 'heads');
    const faceLabel = (f: 'heads' | 'tails') => f === 'heads' ? '앞면' : '뒷면';
    return (
      <div className="mb-reveal-pop">
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6 }}>내 선택</div>
            <CoinFace face={myFace} size={62} />
            <div style={{ color: '#67e8f9', fontSize: 13, fontWeight: 800, marginTop: 6 }}>{faceLabel(myFace)}</div>
          </div>
          <div style={{ color: '#94a3b8', fontSize: 24 }}>→</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6 }}>결과 면</div>
            <CoinFace face={resultFace} size={62} selected />
            <div style={{ color: '#fbbf24', fontSize: 13, fontWeight: 800, marginTop: 6 }}>{faceLabel(resultFace)}</div>
          </div>
        </div>
        <div style={{ marginTop: 12, color: hit ? '#86efac' : '#f87171', fontWeight: 800, fontSize: 18 }}>
          {hit ? '✓ 적중!' : '✗ 미적중'}
        </div>
      </div>
    );
  }

  // E6 운명의 카드 — 선택 카드만 뒤집어 공개 (다른 카드 공개 X)
  if (kind === 'E6' && typeof chosen === 'number') {
    const cardColor = delta > 0 ? '#86efac' : delta < 0 ? '#f87171' : '#cbd5e1';
    return (
      <div className="mb-reveal-pop">
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>선택한 카드가 뒤집어졌습니다</div>
        <div className="mb-card-reveal-flip" style={{
          display: 'inline-block',
          width: 100, aspectRatio: '2/3', borderRadius: 10,
          background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%)',
          border: '3px solid #d97706',
          padding: 12,
          boxShadow: '0 8px 20px rgba(251,191,36,0.4), inset 0 2px 4px rgba(255,255,255,0.4)',
        }}>
          <div style={{ color: '#78350f', fontSize: 11, fontWeight: 700 }}>카드 {chosen + 1}</div>
          <div style={{ marginTop: 24, fontSize: 32, fontWeight: 900, color: cardColor === '#86efac' ? '#065f46' : cardColor === '#f87171' ? '#7f1d1d' : '#334155' }}>
            {delta > 0 ? '+' : ''}{delta}
          </div>
        </div>
      </div>
    );
  }

  // E7 홀짝 — 내 선택 + 적중/미적중 (outcomeLabel 정확 매칭, 숫자 공개 X)
  // §QA6: E7 추첨 숫자 공개 (drawnNumber + parity + 적중/미적중). 서버 drawnNumber 가 단일 진실 공급원.
  if (kind === 'E7' && typeof chosen === 'string') {
    const myPick = chosen === 'odd' ? '홀' : '짝';
    const hit = outcomeLabel === '적중';
    const judgedParity: '홀' | '짝' | null = drawnNumber != null
      ? (drawnNumber % 2 === 1 ? '홀' : '짝')
      : null;
    return (
      <div className="mb-reveal-pop">
        {drawnNumber != null && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 2 }}>추첨 숫자</div>
            <div style={{
              fontSize: 56, fontWeight: 900, color: '#fde68a',
              textShadow: '0 2px 10px rgba(251,191,36,0.4)',
              lineHeight: 1.1,
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            }}>{drawnNumber}</div>
            <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 2 }}>
              = <b style={{ color: judgedParity === '홀' ? '#fca5a5' : '#67e8f9', fontSize: 15 }}>{judgedParity}</b>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#94a3b8', fontSize: 11 }}>내 선택</div>
            <div style={{
              fontSize: 28, fontWeight: 900, marginTop: 4,
              color: myPick === '홀' ? '#fca5a5' : '#67e8f9',
            }}>{myPick}</div>
          </div>
          <div style={{ color: '#94a3b8', fontSize: 20 }}>vs</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#94a3b8', fontSize: 11 }}>판정</div>
            <div style={{
              fontSize: 28, fontWeight: 900, marginTop: 4,
              color: judgedParity === '홀' ? '#fca5a5' : '#67e8f9',
            }}>{judgedParity ?? '—'}</div>
          </div>
        </div>
        <div style={{ marginTop: 12, color: hit ? '#86efac' : '#f87171', fontWeight: 800, fontSize: 18 }}>
          {hit ? '✓ 적중!' : '✗ 미적중'}
        </div>
      </div>
    );
  }

  // E1 확정 vs 랜덤 — 선택 문 + 공개
  if (kind === 'E1' && typeof chosen === 'string') {
    const myDoor = chosen as 'A' | 'B';
    const label = myDoor === 'A' ? '확정 보상' : '랜덤 보상';
    return (
      <div className="mb-reveal-pop">
        <div style={{ color: '#94a3b8', fontSize: 12 }}>선택한 문</div>
        <div style={{ color: myDoor === 'A' ? '#86efac' : '#c4b5fd', fontSize: 24, fontWeight: 900, marginTop: 4 }}>
          {myDoor} · {label}
        </div>
        <div style={{ marginTop: 10, color: '#fbbf24', fontSize: 15, fontWeight: 700 }}>
          {outcomeLabel ?? '공개'}
        </div>
      </div>
    );
  }

  // E3 보물상자 — 선택 상자 열림
  if (kind === 'E3' && typeof chosen === 'string') {
    return (
      <div className="mb-reveal-pop">
        <div style={{ color: '#94a3b8', fontSize: 12 }}>선택한 상자</div>
        <div style={{ color: '#fbbf24', fontSize: 36, fontWeight: 900, marginTop: 4 }}>{chosen}</div>
        <div style={{ marginTop: 10, color: '#f1f5f9', fontSize: 15 }}>
          🎁 {outcomeLabel ?? '보상 공개'}
        </div>
      </div>
    );
  }

  // E5 폭탄 — 선택 선 + 안전/폭발
  if (kind === 'E5' && (typeof chosen === 'number' || typeof chosen === 'string')) {
    const isSafe = delta > 0;
    return (
      <div className="mb-reveal-pop">
        <div style={{ color: '#94a3b8', fontSize: 12 }}>선택한 선</div>
        <div style={{ color: '#fbbf24', fontSize: 32, fontWeight: 900, marginTop: 4 }}>{chosen}</div>
        <div style={{
          marginTop: 10,
          color: isSafe ? '#86efac' : '#f87171',
          fontWeight: 800, fontSize: 20,
        }}>
          {isSafe ? '✓ 안전!' : '💥 폭발!'}
        </div>
        <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 4 }}>{outcomeLabel}</div>
      </div>
    );
  }

  // 보너스 금고 — 선택 금고 공개
  if (kind === 'vault' && typeof chosen === 'string') {
    const isSafe = chosen === 'A';
    return (
      <div className="mb-reveal-pop">
        <div style={{ color: '#94a3b8', fontSize: 12 }}>열어본 금고</div>
        <div style={{ color: '#fbbf24', fontSize: 20, fontWeight: 900, marginTop: 4 }}>
          {isSafe ? '🔒 안전한 금고' : '🎲 행운의 금고'}
        </div>
        <div style={{ marginTop: 10, color: '#f1f5f9', fontSize: 15, fontWeight: 700 }}>
          {outcomeLabel ?? '보상 공개'}
        </div>
      </div>
    );
  }

  // 상점 — 선택 결과
  if (kind === 'shop' && typeof chosen === 'string') {
    const map: Record<string, string> = {
      shield: '🛡 방패 획득',
      speedBoost: '🚀 가속 획득',
      pass: '상점 통과',
    };
    return (
      <div className="mb-reveal-pop">
        <div style={{ color: '#c4b5fd', fontSize: 20, fontWeight: 800 }}>{map[chosen] ?? outcomeLabel ?? '처리 완료'}</div>
      </div>
    );
  }

  // E8 방패 (즉시) — 효과/사용/보유 3블록 (사용자 지시 §2.2)
  // QA Issue #4: BE가 shieldsCapReached=true 로 명시하면 "최대 보유 중 · 추가 획득 실패" 메인 UI 로 분기
  if (kind === 'E8') {
    const owned = shieldsOwned ?? 0;
    const capReached = shieldsCapReached === true;
    if (capReached) {
      return (
        <div className="mb-reveal-pop">
          <div style={{ fontSize: 56, opacity: 0.65 }}>🛡</div>
          <div style={{ color: '#fbbf24', fontSize: 18, fontWeight: 800, marginTop: 6 }}>
            방패 최대 보유 중 · 추가 획득 실패
          </div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 8 }}>
            이미 <b style={{ color: '#fbbf24' }}>{owned} / 3</b> 보유 중입니다. 추가 획득 없이 다음 턴으로 진행합니다.
          </div>
        </div>
      );
    }
    return (
      <div className="mb-reveal-pop">
        <div className="mb-shield-grow" style={{ fontSize: 56 }}>🛡</div>
        <div style={{ color: '#c4b5fd', fontSize: 20, fontWeight: 800, marginTop: 6 }}>방패 획득!</div>
        <div style={shieldInfoBox}>
          <div style={shieldInfoRow}>
            <span style={shieldInfoLabel}>💡 효과</span>
            <span style={shieldInfoValue}>감점 이벤트에서 손실을 <b>1회 완전 방어</b>합니다.</span>
          </div>
          <div style={shieldInfoRow}>
            <span style={shieldInfoLabel}>⚙ 사용</span>
            <span style={shieldInfoValue}>감점 발생 즉시 <b>자동 소모</b> (수동 조작 없음).</span>
          </div>
          <div style={shieldInfoRow}>
            <span style={shieldInfoLabel}>📦 보유</span>
            <span style={shieldInfoValue}>
              현재 <b style={{ color: '#c4b5fd' }}>{owned} / 3</b>
            </span>
          </div>
        </div>
      </div>
    );
  }

  // E10 잭팟 (즉시, 등급 라벨만 — 가짜 reel 조합 X)
  if (kind === 'E10') {
    const isJackpot = delta >= 600;
    const isBig = delta >= 200;
    return (
      <div className="mb-reveal-pop">
        <div style={{ fontSize: 56, filter: isJackpot ? 'drop-shadow(0 0 12px rgba(251,191,36,0.8))' : 'none' }}>💎</div>
        <div style={{
          color: '#fbbf24', fontSize: isJackpot ? 24 : isBig ? 20 : 16, fontWeight: 900, marginTop: 6,
        }}>
          {outcomeLabel ?? '결과'}
        </div>
      </div>
    );
  }

  // E9 워프 — 목적지 라벨
  if (kind === 'E9') {
    return (
      <div className="mb-reveal-pop">
        <div style={{ fontSize: 56 }}>🌀</div>
        <div style={{ color: '#f472b6', fontSize: 16, fontWeight: 700, marginTop: 6 }}>
          {outcomeLabel ?? '워프 완료'}
        </div>
      </div>
    );
  }

  // safe 셀 (START 등 — 무인도 결과는 outcomeLabel 에 포함)
  if (kind === 'safe') {
    return (
      <div className="mb-reveal-pop">
        <div style={{ color: '#94a3b8', fontSize: 12 }}>결과</div>
        <div style={{ color: '#f1f5f9', fontSize: 15, fontWeight: 700, marginTop: 4 }}>
          {outcomeLabel ?? '안전'}
        </div>
      </div>
    );
  }

  // 기본 fallback
  return (
    <div className="mb-reveal-pop">
      <div style={{ color: '#94a3b8', fontSize: 13 }}>{outcomeLabel ?? '결과'}</div>
    </div>
  );
}

// 가위바위보 상대 패 역산 (choice + 승패 → 상대 손 결정론)
function inferRpsOpponent(my: 'rock' | 'paper' | 'scissors', outcomeLabel?: string): 'rock' | 'paper' | 'scissors' {
  if (outcomeLabel === '비김') return my;
  if (outcomeLabel === '승') {
    if (my === 'rock') return 'scissors';
    if (my === 'paper') return 'rock';
    return 'paper';  // scissors
  }
  // 패
  if (my === 'rock') return 'paper';
  if (my === 'paper') return 'scissors';
  return 'rock';  // scissors
}
function rpsEmoji(h: 'rock' | 'paper' | 'scissors'): string {
  return h === 'rock' ? '✊' : h === 'paper' ? '✋' : '✌';
}
function outcomeLabelColor(label?: string): string {
  if (label === '승') return '#86efac';
  if (label === '패') return '#f87171';
  return '#cbd5e1';
}

// ─────────── 워프 도착 블록 (QA4 §8 arrivalKind 분기) ───────────

function WarpScoreBlock({ delta, outcomeLabel }: { delta: number; outcomeLabel: string }) {
  // §QA4 §3: E10 "꽝" (delta=0) 포함 — "0점 · 꽝!" 명시
  const isZero = delta === 0;
  const color = delta > 0 ? '#86efac' : delta < 0 ? '#f87171' : '#fca5a5';
  // outcomeLabel 에서 "워프 도착: " 접두어 제거 (전시 가독성 — 파싱이 아닌 안전 접두어 제거)
  const cleanLabel = outcomeLabel.startsWith('워프 도착: ') ? outcomeLabel.slice(7) : outcomeLabel;
  return (
    <div style={warpArrivalBox}>
      <div style={{ fontSize: 11, color: '#94a3b8' }}>도착 칸 보상{cleanLabel ? ` · ${cleanLabel}` : ''}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 4 }}>
        {isZero ? '0점 · 꽝!' : `${delta > 0 ? '+' : ''}${delta}점`}
      </div>
    </div>
  );
}

function WarpShieldBlock({ shieldsOwnedAfter }: { shieldsOwnedAfter: number }) {
  const atCap = shieldsOwnedAfter >= SHOP_CONFIG.shieldMax;
  return (
    <div style={warpArrivalBox}>
      <div style={{ color: '#c4b5fd', fontSize: 16, fontWeight: 800 }}>🛡 방패 획득!</div>
      <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 6, lineHeight: 1.6 }}>
        감점 이벤트에서 손실을 1회 완전 방어
      </div>
      <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.6 }}>
        감점 발생 즉시 자동 소모
      </div>
      <div style={{ fontSize: 14, color: atCap ? '#fbbf24' : '#c4b5fd', marginTop: 8, fontWeight: 800 }}>
        보유 <b>{shieldsOwnedAfter} / {SHOP_CONFIG.shieldMax}</b>
        {atCap && <span style={{ color: '#94a3b8', fontSize: 10, marginLeft: 6, fontWeight: 600 }}>(최대)</span>}
      </div>
    </div>
  );
}

function WarpTrapBlock() {
  return (
    <div style={{ ...warpArrivalBox, background: 'rgba(217,119,6,0.15)', border: '1px solid rgba(251,191,36,0.4)' }}>
      <div style={{ color: '#fde68a', fontSize: 16, fontWeight: 800 }}>🏝 무인도에 갇혔습니다!</div>
      <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 8, lineHeight: 1.7 }}>
        다음 턴부터 탈출 시도 가능
      </div>
      <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.7 }}>
        첫 {TRAP_CONFIG.guaranteeAfterFails}회 시도: 주사위 <b style={{ color: '#fff' }}>{TRAP_CONFIG.successThreshold}·5·6</b>이면 탈출 성공
      </div>
      <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.7 }}>
        {TRAP_CONFIG.guaranteeAfterFails + 1}번째 시도: <b style={{ color: '#fff' }}>무조건 탈출</b>
      </div>
    </div>
  );
}

// ─────────── 이벤트별 애니메이션 컴포넌트 ───────────

function AnimDoors({ chosen }: { chosen: string }) {
  return (
    <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
      {['A', 'B'].map((id) => {
        const selected = id === chosen;
        return (
          <div key={id} className={selected ? 'mb-door-open' : ''} style={{
            width: 90, height: 120, borderRadius: 10,
            background: id === 'A' ? 'linear-gradient(180deg, #065f46, #10b981)' : 'linear-gradient(180deg, #4c1d95, #a78bfa)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            border: selected ? '3px solid #fbbf24' : '2px solid #334155',
            boxShadow: selected ? '0 0 24px rgba(251,191,36,0.6)' : '0 4px 10px rgba(0,0,0,0.3)',
            position: 'relative', overflow: 'hidden',
            transition: 'all 300ms',
            transformOrigin: id === 'A' ? 'left center' : 'right center',
          }}>
            <div style={{ color: '#fff', fontSize: 36, fontWeight: 900 }}>{id}</div>
            <div style={{ color: id === 'A' ? '#a7f3d0' : '#ddd6fe', fontSize: 11, marginTop: 6 }}>
              {id === 'A' ? '확정' : '랜덤'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AnimRps({ chosen, result, onComplete }: {
  chosen: string;
  result: { delta: number; outcomeLabel?: string } | null;
  onComplete: () => void;
}) {
  // §QA5 §1: 상대 손 순환 → 서버 결과 수신 → 결정론 정지 (inferRpsOpponent 재사용) → 유지 → onComplete
  const myHand = (chosen === 'rock' || chosen === 'paper' || chosen === 'scissors')
    ? (chosen as 'rock' | 'paper' | 'scissors')
    : 'rock';
  const myEmoji = rpsEmoji(myHand);

  // 재마운트 시 result 이미 있으면 즉시 stopped (사용자가 다시 애니메이션 보지 않아도 결과 보여짐)
  const [stopped, setStopped] = useState<boolean>(!!result);
  const [cycleIdx, setCycleIdx] = useState<number>(0);
  const cycleHands: ReadonlyArray<'rock' | 'paper' | 'scissors'> = ['rock', 'paper', 'scissors'];

  // 결정 전: 상대 손 150ms 간격 순환 (결정론 cycleIdx, 랜덤 X)
  useEffect(() => {
    if (stopped) return;
    const t = window.setInterval(() => setCycleIdx(i => (i + 1) % 3), 150);
    return () => window.clearInterval(t);
  }, [stopped]);

  // result 수신 → 400ms 더 순환 후 정지 ("정지하는 느낌")
  useEffect(() => {
    if (result && !stopped) {
      const t = window.setTimeout(() => setStopped(true), 400);
      return () => window.clearTimeout(t);
    }
  }, [result, stopped]);

  // stopped → 700ms 유지 (사용자가 상대 손 명확히 확인) → onComplete 로 ResultStage 전환
  useEffect(() => {
    if (stopped) {
      const t = window.setTimeout(onComplete, 700);
      return () => window.clearTimeout(t);
    }
  }, [stopped, onComplete]);

  // 최종 상대 손 — 서버 결과 역산 (RevealContent E2 와 동일 함수)
  const finalOpponent: 'rock' | 'paper' | 'scissors' | null = result
    ? inferRpsOpponent(myHand, result.outcomeLabel)
    : null;
  const currentOpponent: 'rock' | 'paper' | 'scissors' = stopped && finalOpponent
    ? finalOpponent
    : cycleHands[cycleIdx];
  const oppEmoji = rpsEmoji(currentOpponent);

  return (
    <div style={{ display: 'flex', gap: 24, justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="mb-rps-shake" style={{ fontSize: 64, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>{myEmoji}</div>
        <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>나</div>
      </div>
      <div style={{ color: '#cbd5e1', fontSize: 20, fontWeight: 700 }}>VS</div>
      <div style={{ textAlign: 'center' }}>
        <div
          className={stopped ? 'mb-reveal-pop' : 'mb-rps-shake'}
          style={{ fontSize: 64, animationDelay: stopped ? '0s' : '0.15s', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
        >
          {oppEmoji}
        </div>
        <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>상대</div>
      </div>
    </div>
  );
}

function AnimTreasure({ chosen, pending: _pending, result, onComplete }: {
  chosen: string;
  pending: InProgressEventState;
  result: { delta: number; outcomeLabel?: string } | null;
  onComplete: () => void;
}) {
  // §QA5 §2: stage 머신 — shake(0-400ms) → opening(400-1200ms) → revealing(1200ms+, result 수신 후 pop 1600ms 유지 → onComplete)
  // 서버 응답 지연 시 revealing 단계에서 보상 숨김 상태로 대기 (사용자 지시 "결과 수신 전까지 보상 공개 X")
  const [stage, setStage] = useState<'shake' | 'opening' | 'revealing'>('shake');
  const [showReward, setShowReward] = useState<boolean>(false);

  useEffect(() => {
    const t1 = window.setTimeout(() => setStage('opening'), 400);
    const t2 = window.setTimeout(() => setStage('revealing'), 1200);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, []);

  // revealing 단계 + result 수신 → showReward (뚜껑 열린 상태에서 숫자 pop)
  useEffect(() => {
    if (stage === 'revealing' && result != null && !showReward) {
      setShowReward(true);
    }
  }, [stage, result, showReward]);

  // showReward 후 1600ms 유지 → onComplete 호출 (ResultStage 전환)
  useEffect(() => {
    if (showReward) {
      const t = window.setTimeout(onComplete, 1600);
      return () => window.clearTimeout(t);
    }
  }, [showReward, onComplete]);

  const finalDelta = result?.delta;
  const rewardColor = finalDelta == null ? '#334155'
    : finalDelta > 0 ? '#065f46'
    : finalDelta < 0 ? '#7f1d1d'
    : '#334155';
  const lidOpen = stage !== 'shake';  // Phase B/C 에 뚜껑 열림

  return (
    <div style={{ display: 'flex', gap: 20, justifyContent: 'center', alignItems: 'flex-end', minHeight: 220, flexWrap: 'wrap' }}>
      {['A', 'B', 'C'].map((id) => {
        const selected = id === chosen;
        return (
          // §QA5 §2 보완: wrapper (position layout 전용) + inner 요소 분리 — transform 충돌 방지
          <div key={id} style={{ position: 'relative', width: 92, height: selected ? 200 : 120 }}>
            {/* 반짝임 — Phase A/B 동안만 (보상 숨김 시기) */}
            {selected && (stage === 'shake' || stage === 'opening') && (
              <>
                <div className="mb-sparkle-rise" style={{ position: 'absolute', top: 70, left: 32, fontSize: 20, color: '#fde68a', pointerEvents: 'none' }}>✦</div>
                <div className="mb-sparkle-rise" style={{ position: 'absolute', top: 60, left: 60, fontSize: 14, color: '#fbbf24', animationDelay: '0.2s', pointerEvents: 'none' }}>✧</div>
                <div className="mb-sparkle-rise" style={{ position: 'absolute', top: 80, left: 10, fontSize: 12, color: '#fef3c7', animationDelay: '0.4s', pointerEvents: 'none' }}>✦</div>
              </>
            )}

            {/* 보상 숫자 pop — 상자 안에서 위로 튀어나옴. result 수신 + stage='revealing' 때만 노출.
                position 전용 wrapper (translateX for centering) + 애니메이션 class 는 inner 로 분리 (transform 충돌 방지) */}
            {selected && showReward && finalDelta != null && (
              <div style={{
                position: 'absolute', bottom: 60, left: '50%',
                transform: 'translateX(-50%)',  // layout 전용
                zIndex: 10, pointerEvents: 'none',
              }}>
                <div
                  className="mb-treasure-reward-pop"
                  style={{
                    background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 50%, #d97706 100%)',
                    border: '3px solid #78350f', borderRadius: 12,
                    padding: '8px 16px', color: rewardColor,
                    fontSize: 26, fontWeight: 900,
                    boxShadow: '0 6px 20px rgba(251,191,36,0.5), inset 0 1px 2px rgba(255,255,255,0.4)',
                    whiteSpace: 'nowrap',
                    textShadow: '0 1px 2px rgba(255,255,255,0.3)',
                  }}
                >
                  {finalDelta > 0 ? '+' : ''}{finalDelta}
                </div>
              </div>
            )}

            {/* 뚜껑 — Phase B/C 에 열림 (lidOpen=true 일 때 애니메이션 트리거) */}
            <div
              className={selected && lidOpen ? 'mb-treasure-lid-anim' : ''}
              style={{
                position: 'absolute', bottom: 56, left: 6, right: 6, height: 20,
                borderRadius: '8px 8px 4px 4px',
                background: selected
                  ? 'linear-gradient(180deg, #d97706 0%, #92400e 100%)'
                  : 'linear-gradient(180deg, #475569 0%, #334155 100%)',
                border: `2px solid ${selected ? '#78350f' : '#1e293b'}`,
                borderBottom: 'none',
                boxShadow: selected ? '0 2px 6px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.2)' : '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              {/* 잠금고리 */}
              <div style={{
                position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)',
                width: 10, height: 10, borderRadius: '50%',
                background: selected ? '#fbbf24' : '#64748b',
                border: `1.5px solid ${selected ? '#78350f' : '#1e293b'}`,
              }} />
            </div>

            {/* 본체 — Phase A (shake) 에만 흔들림 */}
            <div
              className={selected && stage === 'shake' ? 'mb-treasure-body-shake' : ''}
              style={{
                position: 'absolute', bottom: 0, left: 6, right: 6, height: 60, borderRadius: 6,
                background: selected
                  ? 'linear-gradient(135deg, #b45309 0%, #d97706 50%, #92400e 100%)'
                  : 'linear-gradient(135deg, #334155 0%, #475569 100%)',
                border: `2px solid ${selected ? '#78350f' : '#1e293b'}`,
                boxShadow: selected ? '0 4px 12px rgba(217,119,6,0.5), inset 0 1px 2px rgba(255,255,255,0.15)' : '0 2px 4px rgba(0,0,0,0.3)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {/* 내부 glow — Phase B 부터 (뚜껑 열림 상태) */}
              {selected && lidOpen && (
                <div style={{
                  position: 'absolute', top: 2, left: 4, right: 4, height: 20,
                  borderRadius: 4,
                  background: 'radial-gradient(ellipse at center, rgba(254,243,199,0.9), rgba(251,191,36,0.4))',
                  animation: 'mb-glow-pulse-gold 900ms ease-in-out infinite',
                }} />
              )}
              <IconE3 size={32} />
              <div style={{ color: selected ? '#fef3c7' : '#64748b', fontSize: 11, fontWeight: 800, marginTop: 2, textShadow: selected ? '0 1px 2px rgba(0,0,0,0.4)' : 'none' }}>{id}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 공용 동전 2면 컴포넌트.
 * face: 앞면(heads=W) / 뒷면(tails=★)
 * selected: 선택 glow
 * size: 지름 (px)
 */
function CoinFace({ face, size = 72, selected = false }: { face: 'heads' | 'tails'; size?: number; selected?: boolean }) {
  const isHeads = face === 'heads';
  const bg = isHeads
    ? 'radial-gradient(circle at 35% 30%, #fde68a 0%, #fbbf24 50%, #b45309 100%)'
    : 'radial-gradient(circle at 35% 30%, #fef3c7 0%, #fbbf24 50%, #92400e 100%)';
  const borderColor = isHeads ? '#92400e' : '#78350f';
  return (
    <div
      className={selected ? 'mb-glow-pulse-gold' : ''}
      style={{
        width: size, height: size, borderRadius: '50%',
        background: bg,
        border: `${selected ? 4 : 3}px solid ${selected ? '#fde68a' : borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: selected
          ? '0 0 20px rgba(251,191,36,0.7), inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2)'
          : 'inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2), 0 4px 10px rgba(0,0,0,0.3)',
        position: 'relative',
      }}
    >
      {isHeads ? (
        <div style={{ color: '#78350f', fontSize: size * 0.42, fontWeight: 900, textShadow: '0 1px 2px rgba(255,255,255,0.4)', lineHeight: 1 }}>W</div>
      ) : (
        <div style={{ fontSize: size * 0.38, filter: 'drop-shadow(0 1px 2px rgba(120,53,15,0.4))', lineHeight: 1 }}>★</div>
      )}
    </div>
  );
}

function AnimCoin({ chosen, result }: { chosen: 'heads' | 'tails' | null; result: { delta: number; outcomeLabel?: string } | null }) {
  // §2: AnimCoin — 공중 회전 후 **최종 면** 에 멈춤 (내 선택 ⊕ 적중여부).
  // 서버 결정 (result) 받기 전에는 반복 회전; 받은 후에는 2바퀴 더 돌고 최종 면 정지.
  const [stopped, setStopped] = useState(false);
  const finalFace: 'heads' | 'tails' = (() => {
    if (!chosen || !result) return 'heads';
    const hit = result.outcomeLabel === '적중';
    if (hit) return chosen;
    return chosen === 'heads' ? 'tails' : 'heads';
  })();

  useEffect(() => {
    if (result && !stopped) {
      // 결과 수신 후 ~1200ms 회전 유지 후 정지
      const t = window.setTimeout(() => setStopped(true), 1200);
      return () => window.clearTimeout(t);
    }
  }, [result, stopped]);

  const transform = stopped
    ? (finalFace === 'heads' ? 'rotateX(1800deg)' : 'rotateX(1980deg)')  // 5바퀴 + 180° (뒷면)
    : undefined;

  const style: React.CSSProperties = stopped
    ? { transform, transition: 'transform 500ms cubic-bezier(0.2, 0.8, 0.3, 1)' }
    : { animation: 'mb-coin-face-flip 1.6s cubic-bezier(0.3, 0.1, 0.3, 1) infinite' };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ position: 'relative', display: 'inline-block', width: 110, height: 130, perspective: '600px' }}>
        <div style={{
          ...style,
          width: 92, height: 92, borderRadius: '50%',
          margin: '0 auto', position: 'relative',
          transformStyle: 'preserve-3d',
        }}>
          {/* 앞면 */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 30%, #fde68a 0%, #fbbf24 50%, #b45309 100%)',
            border: '3px solid #92400e',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2)',
            backfaceVisibility: 'hidden',
          }}>
            <div style={{ color: '#78350f', fontSize: 38, fontWeight: 900, textShadow: '0 1px 2px rgba(255,255,255,0.4)' }}>W</div>
          </div>
          {/* 뒷면 */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 30%, #fef3c7 0%, #fbbf24 50%, #92400e 100%)',
            border: '3px solid #78350f',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2)',
            transform: 'rotateX(180deg)',
            backfaceVisibility: 'hidden',
          }}>
            <div style={{ fontSize: 34, filter: 'drop-shadow(0 1px 2px rgba(120,53,15,0.4))' }}>★</div>
          </div>
        </div>
        {/* 그림자 */}
        <div className="mb-coin-shadow-pulse" style={{
          position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)',
          width: 72, height: 10, borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(0,0,0,0.5), transparent 70%)',
          transformOrigin: 'center',
        }} />
      </div>
      <div style={{ color: '#fbbf24', fontSize: 13, marginTop: 10, fontWeight: 700, letterSpacing: 0.5 }}>
        {stopped ? '결과 공개!' : '동전이 돌아갑니다…'}
      </div>
    </div>
  );
}

function AnimBomb({ chosen }: { chosen: string }) {
  // E5 폭탄 — 3색 전선 (빨/파/노) 중 하나를 선택, 가위가 다가와 절단
  const wires: Array<{ id: string; color: string; label: string; stroke: string }> = [
    { id: '1', color: '#dc2626', label: '빨강', stroke: '#991b1b' },
    { id: '2', color: '#2563eb', label: '파랑', stroke: '#1e3a8a' },
    { id: '3', color: '#fbbf24', label: '노랑', stroke: '#92400e' },
  ];
  return (
    <div style={{ textAlign: 'center' }}>
      {/* 폭탄 몸체 + 전선 */}
      <div style={{ position: 'relative', display: 'inline-block', width: 240, height: 160 }}>
        {/* 폭탄 본체 (중앙 아래) */}
        <div className="mb-bomb-tick" style={{
          position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
          width: 110, height: 70,
          borderRadius: 12,
          background: 'radial-gradient(circle at 30% 30%, #475569 0%, #1e293b 100%)',
          border: '2px solid #0f172a',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5), inset 0 2px 4px rgba(148,163,184,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconE5 size={48} />
          {/* 타이머 LED */}
          <div style={{
            position: 'absolute', top: 6, right: 10,
            padding: '2px 6px', borderRadius: 3,
            background: '#dc2626',
            color: '#fef2f2', fontSize: 10, fontWeight: 900, fontFamily: 'ui-monospace, monospace',
            boxShadow: '0 0 8px rgba(220,38,38,0.8)',
          }}>
            00:03
          </div>
        </div>

        {/* 전선 3가닥 */}
        {wires.map((w, i) => {
          const selected = w.id === chosen;
          const topOffset = 10 + i * 18;
          return (
            <div key={w.id} style={{ position: 'absolute', top: topOffset, left: 10, right: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 22, height: 16, borderRadius: 3,
                background: selected ? w.color : '#334155',
                border: `1.5px solid ${selected ? w.stroke : '#1e293b'}`,
                boxShadow: selected ? `0 0 8px ${w.color}` : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#f1f5f9', fontSize: 9, fontWeight: 800,
              }}>{w.id}</div>
              <div style={{ position: 'relative', flex: 1, height: 6 }}>
                <div
                  className={selected ? 'mb-wire-cut' : ''}
                  style={{
                    position: 'absolute', inset: 0,
                    borderRadius: 3,
                    background: selected
                      ? `linear-gradient(90deg, ${w.color} 0%, ${w.color} 85%, ${w.stroke} 100%)`
                      : 'linear-gradient(90deg, #334155 0%, #475569 100%)',
                    boxShadow: selected ? `0 0 6px ${w.color}` : 'inset 0 1px 2px rgba(0,0,0,0.4)',
                    opacity: selected ? 1 : 0.5,
                  }}
                />
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: selected ? w.color : '#64748b', minWidth: 24 }}>{w.label}</div>
            </div>
          );
        })}

        {/* 가위 (선택된 전선 라인으로 다가와 절단) */}
        {chosen && (() => {
          const idx = parseInt(chosen, 10) - 1;
          if (idx < 0 || idx > 2) return null;
          const topOffset = 10 + idx * 18;
          return (
            <div className="mb-scissors-snap" style={{
              position: 'absolute', top: topOffset - 10, left: 100,
              fontSize: 32, pointerEvents: 'none',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
            }}>
              ✂
            </div>
          );
        })()}
      </div>
      <div style={{ color: '#fca5a5', fontSize: 12, marginTop: 10, fontWeight: 700 }}>
        전선 절단 중…
      </div>
    </div>
  );
}

export function AnimCard({ chosen, result }: { chosen: number; result: { delta: number; outcomeLabel?: string; shieldConsumed?: boolean; grossLoss?: number } | null }) {
  // §QA3 §2: 선택 카드가 3D flip 되어 앞면에 서버 delta 큰 숫자 노출. 다른 카드는 뒷면 유지.
  // result 수신 시 flip. 재마운트 시 (result 있는 상태) 즉시 flipped 상태 (ResultStage 유지).
  const [flipped, setFlipped] = React.useState<boolean>(!!result);
  // V70 QA 재재재재발 #2 수정 — flip 투영 중 결과 텍스트 세로 찌그러짐 방지.
  //   rotateY(90°~180°) 구간에서 앞면 투영 폭 = width × |cos(θ)| 로 x 축 압축 → 자식 텍스트 세로 왜곡.
  //   flip 완료 (= FLIP_DELAY_MS + FLIP_DURATION_MS + 안전 margin) 이후에만 결과 텍스트 reveal.
  //   재마운트 (ResultStage) 시에는 result 있는 상태로 mount → 즉시 가시 (초기값 !!result).
  const FLIP_DELAY_MS = 400;
  const FLIP_DURATION_MS = 700;
  const TEXT_REVEAL_SAFETY_MS = 50;
  const [textVisible, setTextVisible] = React.useState<boolean>(!!result);
  React.useEffect(() => {
    if (!result) {
      // result 소실 → 상태 초기화 (cleanup effect 가 timer 를 지움 — 아래 return 참조).
      setFlipped(false);
      setTextVisible(false);
      return;
    }
    if (flipped && textVisible) {
      // 재마운트 (ResultStage) — 이미 완료 상태, timer 없음.
      return;
    }
    const t1 = window.setTimeout(() => setFlipped(true), FLIP_DELAY_MS);
    const t2 = window.setTimeout(
      () => setTextVisible(true),
      FLIP_DELAY_MS + FLIP_DURATION_MS + TEXT_REVEAL_SAFETY_MS,
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [result, flipped, textVisible]);

  const tilts = ['-6deg', '3deg', '-2deg', '5deg', '-4deg'];
  // QA 재발 수정 §2: 방패 발동 시 카드 앞면에 "원래 손실 (-grossLoss)" 를 표시.
  //   이전엔 delta=0 (방패 흡수 후) 을 표시 → 사용자가 "처음부터 0점" 으로 체감.
  //   방패 발동 UX 흐름: 카드 flip → 원래 손실 노출 → 하단 shield 3단 블록 "방패 발동! 실제 변동 0점".
  const isShielded = result?.shieldConsumed === true && typeof result?.grossLoss === 'number' && result.grossLoss > 0;
  const displayValue = isShielded ? -(result!.grossLoss as number) : (result?.delta ?? null);
  const deltaColor = displayValue == null ? '#334155' : displayValue > 0 ? '#065f46' : displayValue < 0 ? '#7f1d1d' : '#334155';
  const scoreText = displayValue != null ? (displayValue > 0 ? `+${displayValue}` : `${displayValue}`) : '?';
  // V70 QA #2 최종 재설계 — SVG viewBox + textLength 로 완전 교체.
  //   이전 접근 (scale → fontSize → scaleX fallback) 은 모두 측정-축소 chain 로 복잡 + edge clip 가능.
  //   SVG 사양 자체가 "viewBox 안에 모든 glyph 자동 수납" + "textLength lengthAdjust 로 압축" 보장.
  //   측정 로직/state 전부 제거. clip 발생 불가능 (SVG renderer 가 viewBox 밖 그리지 않음).
  //
  //   totalLen 계산: scoreText 길이 + "점" 1자 → viewBox 100 안에 **상대 폭** 결정.
  //   textLength 를 viewBox 폭의 92% 상한으로 clamp → preserveAspectRatio="xMidYMid meet" 로
  //   container 폭에 전체 fit. 짧은 글자는 자연 폭 그대로, 긴 글자는 glyph 압축.
  const totalLen = scoreText.length + 1;  // "+1000" + "점"
  const SVG_VB_W = 100;
  const SVG_VB_H = 26;
  // 짧은 글자는 naturalFit, 긴 글자는 명시 폭 압축. 7자 (-10000점) 이상이면 92 로 강제.
  const svgTextLength = totalLen >= 7 ? 92 : undefined;

  return (
    <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, maxWidth: 360, margin: '0 auto', minHeight: 180, alignItems: 'center' }}>
      {[0,1,2,3,4].map((i) => {
        const isChosen = i === chosen;
        const tilt = tilts[i];
        if (!isChosen) {
          // 비선택 카드: 뒷면 유지 (K1 미승인 — 보상 공개 X)
          return (
            <div
              key={i}
              className="mb-card-lift-scatter"
              style={{
                ...( {['--mb-tilt' as string]: tilt} as React.CSSProperties ),
                aspectRatio: '2/3', borderRadius: 8,
                background: 'linear-gradient(135deg, #17222E 0%, #202D3B 50%, #2C3B4D 100%)',
                border: '2px solid #818cf8',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.2)',
                position: 'relative', overflow: 'hidden',
              }}
            >
              <div style={{ position: 'absolute', inset: 6, border: '1px solid rgba(196,181,253,0.4)', borderRadius: 4 }} />
              <div style={{ fontSize: 22, color: '#c4b5fd', fontWeight: 900 }}>✦</div>
              <div style={{ color: '#e0e7ff', fontSize: 10, marginTop: 4, fontWeight: 700 }}>카드 {i + 1}</div>
            </div>
          );
        }
        // 선택 카드 — 3D flip (뒷면 → 앞면)
        return (
          <div
            key={i}
            className="mb-card-center"
            style={{
              aspectRatio: '2/3',
              perspective: '600px',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <div
              style={{
                position: 'relative', width: '100%', height: '100%',
                transformStyle: 'preserve-3d',
                transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                transition: 'transform 700ms cubic-bezier(0.2, 0.8, 0.3, 1)',
              }}
            >
              {/* 뒷면 — V70 2026-10-19 NGLE 브랜드 Teal 재디자인 */}
              <div style={{
                position: 'absolute', inset: 0, borderRadius: 8,
                background: 'linear-gradient(135deg, #106070 0%, #00A6B5 50%, #2BBECC 100%)',
                border: '2px solid #F5C842',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(16, 96, 112, 0.3), inset 0 1px 2px rgba(255,255,255,0.2)',
                backfaceVisibility: 'hidden',
              }}>
                <div style={{ position: 'absolute', inset: 6, border: '1px solid rgba(245, 200, 66, 0.4)', borderRadius: 4 }} />
                <div style={{ fontSize: 32, color: '#F5C842', fontWeight: 900, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>✦</div>
                <div style={{ color: '#FFFFFF', fontSize: 11, marginTop: 4, fontWeight: 700 }}>카드 {i + 1}</div>
              </div>
              {/* 앞면 — 보상값 큰 숫자 (서버 delta 바인딩).
                  V70 QA 재재재발 #2 수정 (scale 기반 자동 fit):
                    - 큰 폰트 (score 22px, unit 15px) 고정 — 짧은 숫자 가독성 유지.
                    - useLayoutEffect 로 offsetWidth 측정 → scale = min(1, availW / naturalW) → inline-flex 에 transform 적용.
                    - 결과: 짧은 숫자 (scale=1) 큰 폰트 그대로, 긴 숫자 scale 축소 → 글자 모두 완전히 보임 (잘림 없음).
                    - overflow:hidden 유지는 안전망 — scale 결과 pixel 폭이 container 이하여서 실제 clip 안 됨. */}
              <div style={{
                // V70 2026-10-19 — NGLE 브랜드 Teal 재디자인 (화이트/파스텔 teal + Primary Teal 보더)
                position: 'absolute', inset: 0, borderRadius: 8,
                background: 'linear-gradient(135deg, #FFFFFF 0%, #DFF2F4 50%, #C8E6E6 100%)',
                border: '2px solid #00A6B5',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(16, 96, 112, 0.2), inset 0 1px 2px rgba(255,255,255,0.5)',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                padding: 4,
                overflow: 'hidden',
                // V70 QA 재재재재재발 #2: 앞면 레이어 전체 opacity gate.
                //   이전 수정은 "결과 숫자 영역" 만 숨겼으나 "카드 N" 라벨은 노출된 상태로
                //   flip 투영 압축을 받아 사용자가 "세로 찌그러진 글자" 로 체감.
                //   앞면 wrapper 전체 opacity 0 → flip 90°~180° 구간 전체 숨김 (뒷면도 backface-hidden 로 숨김)
                //   → 투영 압축 글자 노출 없음. flip 완료 후 180ms fade-in 으로 자연 reveal.
                //   DOM 은 렌더 유지 → 내부 scale useLayoutEffect offsetWidth 측정 가능.
                opacity: textVisible ? 1 : 0,
                transition: 'opacity 180ms ease-in',
              }}>
                <div style={{ color: '#78350f', fontSize: 10, fontWeight: 800, marginBottom: 2 }}>카드 {i + 1}</div>
                <div style={{
                  width: '100%',
                  display: 'flex', alignItems: 'baseline', justifyContent: 'center',
                }}>
                  {/* V70 QA #2 최종 — SVG viewBox + textLength + preserveAspectRatio.
                      SVG renderer 가 viewBox 안에 모든 glyph 자동 수납. clip 발생 불가.
                      measurement 로직/state 전부 제거. CSS transform/transition 없음 → 중간 상태 없음.
                      textLength (긴 글자 전용) + lengthAdjust="spacingAndGlyphs" 로 glyph 압축.
                      preserveAspectRatio="xMidYMid meet" 로 container 폭에 비율 유지 fit. */}
                  <svg
                    viewBox={`0 0 ${SVG_VB_W} ${SVG_VB_H}`}
                    preserveAspectRatio="xMidYMid meet"
                    width="100%"
                    height={SVG_VB_H}
                    style={{ display: 'block' }}
                    aria-label={`${scoreText}점`}
                  >
                    <text
                      x={SVG_VB_W / 2}
                      y={20}
                      textAnchor="middle"
                      fontSize={20}
                      fontWeight={900}
                      fill={deltaColor}
                      textLength={svgTextLength}
                      lengthAdjust="spacingAndGlyphs"
                      style={{ letterSpacing: '-0.5px' }}
                    >
                      {scoreText}
                      <tspan fontSize={15} fill="#78350f" fontWeight={700} dx={1}>점</tspan>
                    </text>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AnimOddEven({ chosen, result, onComplete }: {
  chosen: string;
  result: { delta: number; outcomeLabel?: string; drawnNumber?: number } | null;
  onComplete: () => void;
}) {
  // §QA6: 1~10 숫자 추첨 애니메이션
  // stage 머신: cycling (50ms 순환) → slowing (수신 후 150/300/500ms 감속) → stopped (큰 숫자 유지)
  //            → parity (N은 홀/짝 라벨) → done (onComplete)
  const drawnNumber = result?.drawnNumber;
  const [stage, setStage] = useState<'cycling' | 'slowing' | 'stopped' | 'parity' | 'done'>(
    () => (result?.drawnNumber != null ? 'stopped' : 'cycling')  // 재마운트 시 즉시 stopped
  );
  const [currentNum, setCurrentNum] = useState<number>(() => result?.drawnNumber ?? 1);
  const completedRef = useRef(false);  // onComplete 중복 호출 방지 (사용자 지시 §4)

  // Phase 1: cycling — 50ms 간격 순환
  useEffect(() => {
    if (stage !== 'cycling') return;
    const t = window.setInterval(() => setCurrentNum(n => (n % 10) + 1), 50);
    return () => window.clearInterval(t);
  }, [stage]);

  // Phase 2: result 수신 → slowing 시작
  useEffect(() => {
    if (stage === 'cycling' && result != null && result.drawnNumber != null) {
      setStage('slowing');
    }
  }, [stage, result]);

  // Phase 3: slowing — 150→300→500ms 감속 3단계 후 stopped
  // QA Issue #2 수정: drawnNumber 로 **수렴**하는 역방향 시퀀스 (drawnNumber-3, -2, -1 → drawnNumber).
  // 이전엔 선형 증가 후 급작 점프 → "엉뚱한 숫자" 지각. 서버 drawnNumber 자체는 변경 없음.
  useEffect(() => {
    if (stage !== 'slowing' || drawnNumber == null) return;
    const timers: number[] = [];
    const intervals = [150, 300, 500];
    const mod10 = (x: number) => ((x - 1 + 10) % 10) + 1;  // 1~10 wrap
    const seq = [mod10(drawnNumber - 3), mod10(drawnNumber - 2), mod10(drawnNumber - 1)];
    const run = (step: number) => {
      if (step >= intervals.length) {
        setCurrentNum(drawnNumber);
        setStage('stopped');
        return;
      }
      setCurrentNum(seq[step]);
      const t = window.setTimeout(() => run(step + 1), intervals[step]);
      timers.push(t);
    };
    run(0);
    return () => { timers.forEach(window.clearTimeout); };
  }, [stage, drawnNumber]);

  // Phase 4: stopped 800ms 유지 → parity (N은 홀/짝 공개)
  useEffect(() => {
    if (stage !== 'stopped') return;
    const t = window.setTimeout(() => setStage('parity'), 800);
    return () => window.clearTimeout(t);
  }, [stage]);

  // Phase 5: parity 1000ms 유지 → done
  useEffect(() => {
    if (stage !== 'parity') return;
    const t = window.setTimeout(() => setStage('done'), 1000);
    return () => window.clearTimeout(t);
  }, [stage]);

  // Phase 6: done → onComplete (중복 호출 방지)
  useEffect(() => {
    if (stage !== 'done') return;
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }, [stage, onComplete]);

  const parity: '홀' | '짝' | null = drawnNumber != null
    ? (drawnNumber % 2 === 1 ? '홀' : '짝')
    : null;
  const parityColor = parity === '홀' ? '#fca5a5' : parity === '짝' ? '#67e8f9' : '#f1f5f9';
  const myPickLabel = chosen === 'odd' ? '홀' : chosen === 'even' ? '짝' : '';

  return (
    <div style={{ textAlign: 'center', minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 0' }}>
      {/* 사용자 선택 — 전 과정 노출 */}
      {myPickLabel && (
        <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 8 }}>
          내 선택: <b style={{ color: chosen === 'odd' ? '#fca5a5' : '#67e8f9' }}>{myPickLabel}</b>
        </div>
      )}
      {/* 큰 숫자 — cycling/slowing: tick 애니메이션, stopped 이후: pop + 금색 glow */}
      <div
        className={stage === 'cycling' || stage === 'slowing' ? 'mb-oddeven-tick' : 'mb-reveal-pop'}
        style={{
          fontSize: 88, fontWeight: 900,
          color: stage === 'cycling' || stage === 'slowing' ? '#f1f5f9' : '#fde68a',
          textShadow: stage !== 'cycling' && stage !== 'slowing'
            ? '0 2px 12px rgba(251,191,36,0.5), 0 0 24px rgba(251,191,36,0.3)'
            : 'none',
          lineHeight: 1,
          minWidth: 110,
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        }}
      >
        {currentNum}
      </div>
      {/* parity/done: 홀/짝 공개 */}
      {(stage === 'parity' || stage === 'done') && drawnNumber != null && parity && (
        <div className="mb-reveal-pop" style={{ marginTop: 12, fontSize: 20, fontWeight: 800, color: '#cbd5e1' }}>
          <b style={{ color: '#fde68a' }}>{drawnNumber}</b>은(는) <b style={{ color: parityColor, fontSize: 24 }}>{parity}</b>!
        </div>
      )}
    </div>
  );
}

function AnimShield() {
  return (
    <div className="mb-shield-grow" style={{ textAlign: 'center' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 100, height: 100, borderRadius: '50%',
        background: 'radial-gradient(circle at 40% 30%, rgba(167,139,250,0.4), transparent 70%)',
      }}>
        <IconE8 size={88} />
      </div>
      <div style={{ color: '#c4b5fd', fontWeight: 700, marginTop: 12, fontSize: 16 }}>방패 획득!</div>
      <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>다음 감점 이벤트에서 자동 사용</div>
    </div>
  );
}

function AnimWarp() {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="mb-warp-portal" style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 110, height: 110,
      }}>
        <IconE9 size={100} />
      </div>
      <div style={{ color: '#f472b6', fontWeight: 700, marginTop: 10 }}>워프 중…</div>
    </div>
  );
}

function AnimJackpot({ result, onComplete }: {
  result: { delta: number; outcomeLabel?: string } | null;
  onComplete: () => void;
}) {
  // §QA3 §4: 상태 머신 — idle → spinning → stopped → onComplete (result phase)
  // 레버 당기기 전까지 reel 정지. 사용자 조작 완료 후 1.2초 유지 → onComplete.
  const [stage, setStage] = React.useState<'idle' | 'spinning' | 'stopped'>('idle');
  const btnRef = React.useRef<HTMLButtonElement | null>(null);

  const handleLever = React.useCallback(() => {
    if (stage !== 'idle') return;  // 중복 클릭 방지
    setStage('spinning');
    // 2.5초 후 자동 정지 (서버 결정값에 멈춤)
    window.setTimeout(() => setStage('stopped'), 2500);
    // 다시 1.2초 후 result phase 로 전환
    window.setTimeout(() => onComplete(), 2500 + 1200);
  }, [stage, onComplete]);

  React.useEffect(() => {
    if (stage === 'idle') btnRef.current?.focus();
  }, [stage]);

  // reel 후보: E10_CONFIG.dist 의 실제 보상값 (+600, +200, +50, 0)
  const candidates = E10_CONFIG.dist.map(d => d.value);
  const finalValue = result?.delta ?? 0;
  const finalLabel = result?.outcomeLabel ?? E10_CONFIG.dist.find(d => d.value === finalValue)?.label ?? '';
  const isZero = finalValue === 0;

  return (
    <div style={{ textAlign: 'center', minHeight: 240 }}>
      {/* 잭팟 기계 외곽 */}
      <div style={{
        display: 'inline-block',
        padding: '12px 16px 16px',
        background: 'linear-gradient(180deg, #4c1d95 0%, #312e81 100%)',
        border: '3px solid #fbbf24',
        borderRadius: 14,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5), inset 0 2px 4px rgba(255,255,255,0.1)',
      }}>
        {/* 디스플레이 라벨 */}
        <div style={{ marginBottom: 10, fontSize: 11, fontWeight: 800, color: '#fde68a', letterSpacing: 2 }}>
          💎 JACKPOT 💎
        </div>
        {/* reel 3개 */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          {[0, 1, 2].map((i) => (
            <JackpotReel
              key={i}
              candidates={candidates}
              stage={stage}
              finalValue={finalValue}
              reelDelay={i * 150}
            />
          ))}
        </div>
        {/* 최종 라벨 */}
        {stage === 'stopped' && (
          <div className="mb-reveal-pop" style={{ marginTop: 10 }}>
            <div style={{
              fontSize: 20, fontWeight: 900,
              color: isZero ? '#fca5a5' : '#fef3c7',
              textShadow: '0 2px 6px rgba(0,0,0,0.6)',
            }}>
              {isZero ? '0점 · 꽝!' : (finalValue > 0 ? `+${finalValue}점 · ${finalLabel}` : `${finalValue}점 · ${finalLabel}`)}
            </div>
          </div>
        )}
      </div>

      {/* 레버 당기기 버튼 */}
      <div style={{ marginTop: 16 }}>
        {stage === 'idle' ? (
          <button
            ref={btnRef}
            onClick={handleLever}
            aria-label="잭팟 레버 당기기"
            style={{
              padding: '12px 32px',
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              color: '#fff', fontWeight: 900, fontSize: 16,
              border: '3px solid #fef3c7', borderRadius: 999,
              cursor: 'pointer', letterSpacing: 1,
              boxShadow: '0 6px 16px rgba(220,38,38,0.5), inset 0 2px 4px rgba(255,255,255,0.3)',
              outline: 'none',
            }}
            onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px #fde68a, 0 6px 16px rgba(220,38,38,0.5)'; }}
            onBlur={(e) => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(220,38,38,0.5), inset 0 2px 4px rgba(255,255,255,0.3)'; }}
          >
            🎰 레버 당기기
          </button>
        ) : (
          <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: 14, padding: '12px 0' }}>
            {stage === 'spinning' ? '릴이 돌아갑니다…' : '결과 확정!'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 잭팟 reel — idle: 정지(???), spinning: 반복 회전, stopped: finalValue 에 정지.
 * reelDelay 로 3개가 순차적으로 정지하는 느낌 (하지만 모두 finalValue 로 수렴).
 */
function JackpotReel({ candidates, stage, finalValue, reelDelay }: {
  candidates: readonly number[]; stage: 'idle' | 'spinning' | 'stopped'; finalValue: number; reelDelay: number;
}) {
  const ITEM_H = 48;
  const COLS = candidates.length;
  const finalIdx = Math.max(0, candidates.indexOf(finalValue));

  const style: React.CSSProperties = (() => {
    if (stage === 'idle') {
      return { transform: 'translateY(0)' };
    }
    if (stage === 'spinning') {
      return { animation: `mb-roulette-spin 300ms linear infinite` };
    }
    // stopped: 4바퀴 + finalIdx 로 translateY (서버 결정값 정지)
    return {
      transform: `translateY(${-((COLS * 4 + finalIdx) * ITEM_H)}px)`,
      transition: `transform ${1000 + reelDelay}ms cubic-bezier(0.1, 0.4, 0.2, 1)`,
    };
  })();

  const fmt = (v: number) => v === 0 ? '0' : v > 0 ? `+${v}` : `${v}`;

  return (
    <div style={{
      width: 72, borderRadius: 8,
      background: 'linear-gradient(180deg, #1e1b4b, #312e81)',
      border: '2px solid #fbbf24',
      padding: 3,
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
    }}>
      <div style={{ overflow: 'hidden', height: ITEM_H, borderRadius: 4 }}>
        {stage === 'idle' ? (
          <div style={{
            height: ITEM_H, lineHeight: `${ITEM_H}px`, textAlign: 'center',
            fontSize: 20, color: '#64748b', fontWeight: 900,
          }}>?</div>
        ) : (
          <div style={style}>
            {Array.from({ length: 5 }).map((_, bloc) => (
              candidates.map((v, i) => (
                <div key={`${bloc}-${i}`} style={{
                  height: ITEM_H, lineHeight: `${ITEM_H}px`, textAlign: 'center',
                  fontSize: 18, fontWeight: 900,
                  color: v === 0 ? '#fca5a5' : v > 0 ? '#86efac' : '#f87171',
                  background: v > 0 ? 'rgba(16,185,129,0.1)' : v < 0 ? 'rgba(239,68,68,0.1)' : 'rgba(100,116,139,0.1)',
                }}>
                  {fmt(v)}
                </div>
              ))
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AnimVault({ chosen }: { chosen: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="mb-vault-open" style={{
        width: 120, height: 110, borderRadius: 12,
        background: chosen === 'A'
          ? 'linear-gradient(135deg, #064e3b, #10b981)'
          : 'linear-gradient(135deg, #78350f, #fbbf24)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: '3px solid #fcd34d',
        boxShadow: '0 0 24px rgba(251,191,36,0.5), inset 0 2px 4px rgba(255,255,255,0.2)',
      }}>
        <IconVault size={76} />
      </div>
      <div style={{ color: '#fbbf24', fontWeight: 700, marginTop: 12 }}>
        {chosen === 'A' ? '안전한 금고 개봉…' : '행운의 금고 개봉…'}
      </div>
    </div>
  );
}

function AnimShop() {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="mb-shop-counter">
        <IconShop size={88} />
      </div>
      <div style={{ color: '#c4b5fd', fontWeight: 700, marginTop: 8 }}>처리 중…</div>
    </div>
  );
}


// ─────────── 스타일 ───────────

// V70 2026-10-19 — 결과 팝업 중앙 정렬 (하드코딩 금지, 실제 Sidebar 측정 기반).
//   left 는 MarbleEventModal 안에서 useAppShellSidebarWidth() 로 실측하여 override.
//   하드코딩된 fallback 값은 0 (사이드바 미식별 시 뷰포트 전체 중앙).
const backdrop: React.CSSProperties = {
  position: 'fixed', top: 0, bottom: 0, left: 0, right: 0,
  background: 'rgba(2,6,23,0.78)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const card: React.CSSProperties = {
  background: '#0b1220', border: '1px solid #334155', borderRadius: 14,
  padding: 24, maxWidth: 460, width: '100%', margin: '0 auto', color: '#e2e8f0',
  maxHeight: '92vh', overflowY: 'auto', textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const titleRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
};
const introBox: React.CSSProperties = {
  textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 14,
};
const hint: React.CSSProperties = {
  color: '#94a3b8', fontSize: 12, margin: '0 0 12px', lineHeight: 1.6,
};
const errBox: React.CSSProperties = {
  marginTop: 10, padding: 8, background: 'rgba(248,113,113,0.1)',
  border: '1px solid rgba(248,113,113,0.4)', color: '#fca5a5', borderRadius: 6, fontSize: 12,
};
const nextBtn: React.CSSProperties = {
  display: 'inline-block',
  marginTop: 20, padding: '10px 28px',
  background: 'linear-gradient(135deg, #06b6d4, #22d3ee)', color: '#042f3b',
  fontWeight: 700, border: 0, borderRadius: 8, cursor: 'pointer',
  fontSize: 14, letterSpacing: 0.5,
};
const scoreSummaryBox: React.CSSProperties = {
  marginTop: 16, padding: 12,
  background: '#020617', border: '1px solid #1e293b', borderRadius: 8,
  textAlign: 'left', fontSize: 13,
  maxWidth: 340, margin: '16px auto 0',
};
const scoreRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', padding: '2px 0',
};
const scoreNum: React.CSSProperties = {
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
};
const warpArrivalBox: React.CSSProperties = {
  marginTop: 14, padding: '10px 14px',
  background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(167,139,250,0.3)',
  borderRadius: 10, maxWidth: 280,
  marginLeft: 'auto', marginRight: 'auto',
};

const shieldInfoBox: React.CSSProperties = {
  marginTop: 16, padding: '12px 16px',
  background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(167,139,250,0.25)',
  borderRadius: 10, textAlign: 'left',
};
const shieldInfoRow: React.CSSProperties = {
  display: 'flex', gap: 10, alignItems: 'flex-start',
  padding: '6px 0', fontSize: 12, lineHeight: 1.5,
};
const shieldInfoLabel: React.CSSProperties = {
  color: '#a78bfa', fontWeight: 700, flexShrink: 0, minWidth: 50,
};
const shieldInfoValue: React.CSSProperties = {
  color: '#cbd5e1', flex: 1,
};
function optBtn(theme: 'cyan' | 'violet' | 'magenta' | 'yellow', center: boolean = false): React.CSSProperties {
  const borderColor = theme === 'cyan' ? '#164e63' : theme === 'violet' ? '#4c1d95' : theme === 'magenta' ? '#9d174d' : '#92400e';
  return {
    padding: 12, background: '#0f172a', border: `1px solid ${borderColor}`, borderRadius: 8,
    color: '#f1f5f9', cursor: 'pointer', textAlign: center ? 'center' : 'left', fontSize: 14,
  };
}
